import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { eventChannel } from 'redux-saga';
import { all, call, delay, put, race, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { concat, find, isEmpty, isNil, propEq, slice, uniq, uniqBy } from 'ramda';
import isURL from 'validator/lib/isURL';
import queryString from 'query-string';
import Defaults from 'mangools-commons/dist/constants/Defaults';
import Urls from 'mangools-commons/dist/constants/Urls';
import DownloaderService from 'mangools-commons/dist/services/DownloaderService';
import FileService from 'mangools-commons/dist/services/FileService';
import ErrorCodes, {
    INTERNAL_TIMEOUT_ERROR_PAYLOAD,
    INTERNAL_UNCAUGHT_ERROR_PAYLOAD,
} from 'mangools-commons/dist/constants/ErrorCodes';

import { APP_CONFIG } from 'mangools-commons/dist/configs/app';

import { ListCreatedAlert, ListUpdatedAlert } from 'components/other/Alerts';

import AnnouncementsSource from 'sources/AnnouncementsSource';
import KeywordsByDomainResultSource from 'sources/KeywordsByDomainResultSource';
import CompetitorsByDomainResultSource from 'sources/CompetitorsByDomainResultSource';
import KeywordGapResultSource from 'sources/KeywordGapResultSource';
import GoogleSource from 'sources/GoogleSource';
import HistorySource from 'sources/HistorySource';
import ImportSource from 'sources/ImportSource';
import ListSource from 'sources/ListSource';
import LocationSource from 'sources/LocationSource';
import ResultSource from 'sources/ResultSource';
import SerpResultSource from 'sources/SerpResultSource';
import TrendSource from 'sources/TrendSource';
import VersionSource from 'sources/VersionSource';
import CurrencyRatesSource from 'sources/CurrencyRatesSource';
import UnleashSource from 'sources/UnleashSource';

import { generateKeywords, generateQuestions } from 'services/GoogleQueryGeneratorService';

import ExportService from 'services/ExportService';

import { showErrorNotification, showInfoNotification } from 'sagas/uiSagas';

import { handleUncaught, logError } from 'sagas/errorSagas';

import {
    cancelledDeleteSelectedKeywordsFromCurrentListAction,
    cancelledListsDeleteAction,
    emptyCompetitorsByDomainResultsAction,
    emptyGoogleResultsAction,
    emptyKeywordRankAction,
    emptyKeywordsByDomainResultsAction,
    emptyKeywordGapResultsAction,
    errorAnnouncementsAction,
    errorCompetitorsByDomainResultsAction,
    errorContentTypesAction,
    errorCurrencyRatesAction,
    errorDeleteHistoryAction,
    errorDeleteKdHistoryAction,
    errorDeleteSelectedKeywordsFromCurrentListAction,
    errorGoogleResultsAction,
    errorHistoryAction,
    errorImportAction,
    errorKdHistoryAction,
    errorKeywordsByDomainResultsAction,
    errorKeywordGapResultsAction,
    errorKwOpportunitiesAction,
    errorListKeywordsAction,
    errorListsAction,
    errorListsAddKeywordsAction,
    errorListsCreateAction,
    errorListsDeleteAction,
    errorListsRenameAction,
    errorLocationsAction,
    errorResultsAction,
    errorSearchIntentAction,
    errorSerpFeaturesAction,
    errorSerpResultsAction,
    errorSerpResultsMoreAction,
    errorTrendsAction,
    errorUrlDataAction,
    fetchingAnnouncementsAction,
    fetchingCompetitorsByDomainResultsAction,
    fetchingContentTypesAction,
    fetchingCurrencyRatesAction,
    fetchingGoogleResultsAction,
    fetchingHistoryAction,
    fetchingImportAction,
    fetchingKdHistoryAction,
    fetchingKeywordsByDomainResultsAction,
    fetchingKeywordGapResultsAction,
    fetchingLoadMoreKeywordGapResultsAction,
    fetchingKwOpportunitiesAction,
    fetchingListKeywordsAction,
    fetchingListsAction,
    fetchingListsDeleteAction,
    fetchingListsRenameAction,
    fetchingLoadMoreKeywordsByDomainResultsAction,
    fetchingLocationsAction,
    fetchingResultsAction,
    fetchingSearchIntentAction,
    fetchingSerpFeaturesAction,
    fetchingSerpResultsAction,
    fetchingSerpResultsMoreAction,
    fetchingTrendsAction,
    fetchingUrlDataAction,
    finishedGoogleResultsAction,
    optimisticDeleteKeywordFromListAction,
    optimisticDeleteKeywordsFromListAction,
    receivedAnnouncementsAction,
    receivedCompetitorsByDomainResultsAction,
    receivedContentTypesAction,
    receivedCurrencyRatesAction,
    receivedDeleteHistoryAction,
    receivedDeleteKdHistoryAction,
    receivedHistoryAction,
    receivedImportAction,
    receivedKdHistoryAction,
    receivedKeywordRankAction,
    receivedKeywordsByDomainResultsAction,
    receivedKeywordGapResultsAction,
    receivedLoadMoreKeywordGapResultsAction,
    receivedKwOpportunitiesAction,
    receivedListKeywordsAction,
    receivedListsAction,
    receivedListsAddKeywordsAction,
    receivedListsDeleteAction,
    receivedListsNewAction,
    receivedListsRenameAction,
    receivedLoadMoreKeywordsByDomainResultsAction,
    receivedLocationsAction,
    receivedNoMoreSerpResultsAction,
    receivedNoSerpResultsAction,
    receivedResultsAction,
    receivedSearchIntentAction,
    receivedSerpFeaturesAction,
    receivedSerpResultsAction,
    receivedSerpResultsMoreAction,
    receivedSingleContentTypesAction,
    receivedSingleKwOpportunitiesAction,
    receivedSingleSearchIntentAction,
    receivedSingleSerpFeaturesAction,
    receivedTrendsAction,
    receivedUrlDataAction,
    requestedKeywordsImportAction,
    revertOptimisticDeleteKeywordFromListAction,
    revertOptimisticDeleteKeywordsFromListAction,
    revertUpdateListsAddKeywords,
    skippedCompetitorsByDomainResultsAction,
    skippedHistoryAction,
    skippedKdHistoryAction,
    skippedKeywordsByDomainResultsAction,
    skippedKeywordGapResultsAction,
    skippedLoadMoreKeywordGapResultsAction,
    emptyLoadMoreKeywordGapResultsAction,
    errorLoadMoreKeywordGapResultsAction,
    skippedListsAction,
    skippedLoadMoreKeywordsByDomainResultsAction,
    updateListsAddKeywords,
    receivedKwGapKeywordRankAction,
    bulkSilentRefetchAction,
} from 'actions/dataActions';

import {
    hideLongerSerpLoadingNotification,
    setCurrency,
    setCurrentKeywordId,
    setDashboardLeftLoaderProgress,
    setExporting,
    setFilterActive,
    setKeywordSource,
    setNewVersionNotificationShown,
    setQuickFilterSearch,
    // updateSortingSettings,
    showAccessDeniedMessage,
    showDeleteConfirmationMessage,
    showFailureMessage,
    showLongerSerpLoadingNotification,
    showNoConnectionMessage,
    showPricingMessage,
    showRelativeKdDropdown,
    unselectAllKeywords,
    setDashboardBulkFetchProgress,
    setSortingSettings,
    showUpsellMessage,
} from 'actions/uiActions';

import {
    setDefaultGoogleLanguage,
    setDefaultGoogleLocation,
    setDefaultGoogleQuery,
    setDefaultKeywordsByDomainLocation,
    setDefaultKeywordsByDomainQuery,
} from 'actions/defaultsActions';

import { requestedLimitsAction, setUnleashSessionAction } from 'actions/userActions';

import { requestedNavigationAction } from 'actions/routerActions';

import { setParams } from 'actions/paramsActions';

import { gtmTrack } from 'actions/analyticsActions';

import { keywordSourceResourceTypeSelector, resultsDataSelector } from 'selectors/sharedSelectors';

import {
    competitorsCurrentDataParamsSelector,
    currentDataParamsSelector,
    currentKeywordSelector,
    currentListDetailDataSelector,
    exportSelectedKeywordsFilenameSelector,
    filteredAndSortedResultsDataSelector,
    filteredAndSortedSelectedKeywordIdsSelector,
    filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursSelector,
    filteredAndSortedKeywordGapResultsDataSelector,
    filteredAndSortedSelectedKeywordsKeywordGapSelector,
    filteredAndSortedSelectedKeywordsSelector,
    isAllowedPaginationSelector,
    keywordGapResultsCurrentDataParamsSelector,
    listSelector,
    newCurrentKwIdSelector,
    nextSerpPageSelector,
    trendMetaDataSelector,
} from 'selectors/dataSelectors';

import {
    addToListMessageAddingKeywordIdsSelector,
    comparingBoxUrlProtocolSelector,
    comparingBoxUrlSelector,
    currencySelector,
    currentKeywordIdSelector,
    keywordColumnsSettingsVisibleSelector,
    keywordGapColumnsSettingsVisibleSelector,
    keywordSourceResourceIdSelector,
    newVersionNotificationShownSelector,
    relativeKdActiveSelector,
    sortingSettingsSelector,
} from 'selectors/uiSelectors';

import {
    defaultCompetitorQuerySelector,
    defaultGoogleLanguageSelector,
    defaultGoogleLocationSelector,
    defaultSharedCurrentKeywordGraphSelector,
} from 'selectors/defaultsSelectors';

import {
    accessTokenSelector,
    importLimitSelector,
    limitsFetchedSelector,
    loggedInSelector,
    userPlanSelector,
} from 'selectors/userSelectors';

import { paramsSelector } from 'selectors/paramsSelectors';

import { currentRouteSelector } from 'selectors/routingSelectors';

import ActionTypes from 'constants/ActionTypes';
import KeywordsByDomainTypes from 'constants/KeywordsByDomainTypes';
import DataSourceTypes from 'constants/DataSourceTypes';
import DataSubSourceTypes from 'constants/DataSubSourceTypes';
import DeleteResourceTypes from 'constants/DeleteResourceTypes';
import KeywordGraphTypes from 'constants/KeywordGraphTypes';
import KeywordSourceTypes, { COMPETITOR, RELATED } from 'constants/KeywordSourceTypes';
import RoutePaths from 'constants/RoutePaths';
import Strings from 'constants/Strings';
import { analyticsActions, analyticsEvents } from 'constants/analytics';
import { fork } from '@redux-saga/core/effects';
import KdHistorySource from 'sources/KdHistorySource';
import UrlKdDataSource from 'sources/UrlKdDataSource';
import { urlDataLpsSelector } from 'selectors/urlDataSelectors';
import { ErrorTypes } from 'constants/ErrorTypes';
import ContentTypeSource from 'sources/ContentTypeSource';
import SearchIntentSource from 'sources/SearchIntentSource';
import { toast } from 'react-toastify';
import KwOpportunitiesSource from 'sources/KwOpportunitiesSource';
import SerpFeaturesSource from 'sources/SerpFeaturesSource';
import { KEYWORD_GAP_COLUMNS } from 'constants/keywordColumns';
import SerpBulkResultSource from 'sources/SerpBulkResultSource';

const EXPORT_PREFIX = 'kwfinder_';
const EXPORT_SUFFIX = '_export';
const EMPTY_IMPORT_ERROR = {
    status: 200,
    text: 'No keywords returned from import endpoint.',
};

// Keyword source types where each keyword carries its own location (vs. sharing one location
// from URL params). Used by fetchSerpResults to pick the right location for the SERP request.
const PER_KEYWORD_LOCATION_SOURCES = new Set([
    KeywordSourceTypes.COMPETITOR,
    KeywordSourceTypes.KEYWORD_GAP,
    KeywordSourceTypes.LIST,
    KeywordSourceTypes.IMPORT,
]);

function* resetUiStuff() {
    yield put(setFilterActive(false));
    yield put(unselectAllKeywords());
    yield put(setQuickFilterSearch(''));
}

const fetchContentType = handleUncaught(
    function* fetchContentType(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const options = { accessToken, params: action };
        const { CONTENT_TYPE: isColumnVisible } = yield select(keywordColumnsSettingsVisibleSelector);
        const { CONTENT_TYPE: isColumnVisibleInKeywordGap } = yield select(keywordGapColumnsSettingsVisibleSelector);

        if (!isColumnVisible && !isColumnVisibleInKeywordGap) {
            return;
        }

        yield put(fetchingContentTypesAction());

        const { result, _timeout } = yield race({
            result: call(ContentTypeSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedContentTypesAction(payload));
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorContentTypesAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchContentType, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorContentTypesAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorContentTypesAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorContentTypesAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchContentTypeSage', payload);
                        } else {
                            yield call(fetchContentType, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorContentTypesAction(payload));
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.fetch_content_types_error }),
                            );
                            yield call(logError, 'FetchContentTypeSage', payload);
                        } else {
                            yield call(fetchContentType, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorContentTypesAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield call(logError, 'FetchContentTypeSage', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorContentTypesAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
    },
);

const fetchSerpFeatures = handleUncaught(
    function* fetchSerpFeatures(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const options = { accessToken, params: action };
        const { SERP_FEATURES: isColumnVisible } = yield select(keywordColumnsSettingsVisibleSelector);

        if (!isColumnVisible) {
            return;
        }

        yield put(fetchingSerpFeaturesAction((action?.keywords ?? action?.data ?? []).map(item => item.id)));

        const { result, _timeout } = yield race({
            result: call(SerpFeaturesSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedSerpFeaturesAction(payload));
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorSerpFeaturesAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchSerpFeatures, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorSerpFeaturesAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorSerpFeaturesAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorSerpFeaturesAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchSerpFeaturesSaga', payload);
                        } else {
                            yield call(fetchSerpFeatures, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorSerpFeaturesAction(payload));
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.fetch_serp_features_error }),
                            );
                            yield call(logError, 'FetchSerpFeaturesSaga', payload);
                        } else {
                            yield call(fetchSerpFeatures, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorSerpFeaturesAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield call(logError, 'FetchSerpFeaturesSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorSerpFeaturesAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
    },
);

const fetchSearchIntent = handleUncaught(
    function* fetchSearchIntent(action, retrying = false) {
        try {
            const accessToken = yield select(accessTokenSelector);
            const options = { accessToken, params: action };
            const { SEARCH_INTENT: isColumnVisible } = yield select(keywordColumnsSettingsVisibleSelector);
            const keywordGapColumnsSettingsVisible = yield select(keywordGapColumnsSettingsVisibleSelector);

            const isVisible = keywordGapColumnsSettingsVisible[KEYWORD_GAP_COLUMNS.SEARCH_INTENT];

            if (!isColumnVisible && !isVisible) {
                return;
            }

            yield put(fetchingSearchIntentAction((action?.keywords ?? action?.data ?? []).map(k => k.id)));

            const { result, _timeout } = yield race({
                result: call(SearchIntentSource.getData, options),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedSearchIntentAction(payload));
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying) {
                                yield put(errorSearchIntentAction(payload));
                            } else {
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(fetchSearchIntent, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorSearchIntentAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.UNAUTHORIZED: {
                            yield put(errorSearchIntentAction(payload));
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorSearchIntentAction(payload));

                            if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                                yield put(receivedSearchIntentAction(payload));
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying) {
                                yield put(errorSearchIntentAction(payload));
                                yield call(logError, 'FetchSearchIntentSaga', payload);
                            } else {
                                yield call(fetchSearchIntent, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying) {
                                yield put(errorSearchIntentAction(payload));
                                yield call(logError, 'FetchSearchIntentSaga', payload);
                            } else {
                                yield call(fetchSearchIntent, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorSearchIntentAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.fetch_search_intent_error }));
                yield call(logError, 'FetchSearchIntentSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } catch (error) {
            yield put(errorSearchIntentAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_search_intent_error }));
        }
    },
    function* onError() {
        yield put(errorSearchIntentAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_search_intent_error }));
    },
);

const fetchKwOpportunities = handleUncaught(
    function* fetchKwOpportunities(action, retrying = false) {
        try {
            const accessToken = yield select(accessTokenSelector);
            const options = { accessToken, params: action };

            const { KW_OPPORTUNITIES: isColumnVisible } = yield select(keywordColumnsSettingsVisibleSelector);

            if (!isColumnVisible) {
                return;
            }

            yield put(fetchingKwOpportunitiesAction((action.keywords ?? action.data ?? []).map(k => k.id)));

            const { result, _timeout } = yield race({
                result: call(KwOpportunitiesSource.getData, options),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedKwOpportunitiesAction(payload));
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying) {
                                yield put(errorKwOpportunitiesAction(payload));
                            } else {
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(fetchKwOpportunities, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorKwOpportunitiesAction(payload));
                            yield put(showAccessDeniedMessags());
                            break;
                        }
                        case ErrorCodes.UNAUTHORIZED: {
                            yield put(errorKwOpportunitiesAction(payload));
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorKwOpportunitiesAction(payload));

                            if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                                yield put(receivedKwOpportunitiesAction(payload));
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying) {
                                yield put(errorKwOpportunitiesAction(payload));
                                yield call(logError, 'FetchKwOpportunitiesSaga', payload);
                            } else {
                                yield call(fetchKwOpportunities, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying) {
                                yield put(errorKwOpportunitiesAction(payload));
                                yield call(logError, 'FetchKwOpportunitiesSaga', payload);
                            } else {
                                yield call(fetchKwOpportunities, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorKwOpportunitiesAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.fetch_kw_opportunities_error }));
                yield call(logError, 'FetchKwOpportunitiesSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } catch (error) {
            yield put(errorKwOpportunitiesAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_kw_opportunities_error }));
        }
    },
    function* onError() {
        yield put(errorKwOpportunitiesAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_kw_opportunities_error }));
    },
);

const fetchResults = handleUncaught(
    function* fetchResults(action, retrying = false) {
        yield call(resetUiStuff);
        yield put(fetchingResultsAction());

        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const options = { accessToken, params };

        const { result, _timeout } = yield race({
            result: call(ResultSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(
                    setKeywordSource({
                        id: null,
                        name: null,
                        type: KeywordSourceTypes.RELATED,
                    }),
                );

                yield put(receivedResultsAction(payload));
                const plan = yield select(userPlanSelector);

                const toRun = [
                    call(fetchContentType, payload),
                    call(fetchSerpFeatures, payload),
                    call(fetchSearchIntent, payload),
                    call(fetchKwOpportunities, payload),
                ];

                if (plan === 'trial') {
                    toRun.push(fork(bulkSilentFetchSerpResults));
                }

                yield all(toRun);
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        // No logged in users, showing fake data, no special message
                        yield put(errorResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.UNPROCESSABLE_ENTITY: {
                        yield put(errorResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchResultsSaga', payload);
                        } else {
                            yield call(fetchResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
                            yield call(logError, 'FetchResultsSaga', payload);
                        } else {
                            yield call(fetchResults, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
            yield call(logError, 'FetchResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
    },
);

/* eslint-disable max-len */
const fetchSerpResults = handleUncaught(
    function* fetchSerpResults(options = { disableCache: false, retrying: false }, action) {
        yield put(fetchingSerpResultsAction());

        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const currentKeyword = yield select(currentKeywordSelector);
        const keywordSourceType = yield select(keywordSourceResourceTypeSelector);
        // Google-driven sources share one location (from URL/params). For per-keyword location
        // sources (domain, keyword gap, list, import) we fall back to the keyword's own location.
        // Without this guard a stale currentKeyword leaks the previous search's location into a
        // freshly issued search — see ITRBU-546.
        const locationId = PER_KEYWORD_LOCATION_SOURCES.has(keywordSourceType)
            ? (currentKeyword?.location?.id ?? params.locationId)
            : params.locationId;
        const requestOptions = { accessToken, disableCache: options.disableCache, page: 0, params: { ...params, locationId } };

        const { result, _timeout } = yield race({
            result: call(SerpResultSource.getData, requestOptions),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        // Hide longer loading notification if this was retry
        // does not matter how it ended up.
        if (options.retrying === true) {
            yield put(hideLongerSerpLoadingNotification());
        }

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                if (isNil(payload)) {
                    // No content means no SERP results for this keyword
                    yield put(receivedNoSerpResultsAction());
                } else {
                    yield put(receivedSerpResultsAction(payload));
                    yield put(receivedSingleKwOpportunitiesAction({ [payload.kwId]: payload.kwOpps }));
                    yield put(receivedSingleSearchIntentAction(payload.searchIntent));
                    yield put(receivedSingleSerpFeaturesAction({ [payload.kwId]: payload.serpFeatures }));
                }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (options.retrying === true) {
                            yield put(errorSerpResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResults, { disableCache: false, retrying: true }, action);
                        }
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorSerpResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } // else if (payload.type === ErrorTypes.CREDIT_LIMIT) {}

                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorSerpResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        // No logged in users, showing fake data, no special message
                        yield put(errorSerpResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.REQUEST_TIMEOUT: {
                        if (options.retrying === true) {
                            yield put(errorSerpResultsAction(payload));
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }),
                            );
                            yield call(logError, 'FetchSerpResultsSaga', payload);
                        } else {
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResults, { disableCache: false, retrying: true }, action);
                        }
                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (options.retrying === true) {
                            yield put(errorSerpResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchSerpResultsSaga', payload);
                        } else {
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResults, { disableCache: false, retrying: true }, action);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (options.retrying === true) {
                            yield put(errorSerpResultsAction(payload));
                            yield put(
                                showFailureMessage({
                                    // A 500 carries retry_after only when every SERP provider is
                                    // down; then the wait is real information, not a generic error.
                                    details: isNil(payload.retryAfter)
                                        ? Strings.messages.failure.fetch_serp_results_error
                                        : Strings.messages.failure.serp_unavailable_error.replace(
                                              '{seconds}',
                                              payload.retryAfter,
                                          ),
                                }),
                            );
                            yield call(logError, 'FetchSerpResultsSaga', payload);
                        } else {
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResults, { disableCache: false, retrying: true }, action);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorSerpResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }));
            yield call(logError, 'FetchSerpResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorSerpResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }));
    },
);
/* eslint-enable max-len */

const bulkSilentFetchSerpResults = handleUncaught(
    function* bulkSilentFetchSerpResults() {
        const sortedAndFiltered = yield select(filteredAndSortedResultsDataSelector);
        const sourceType = yield select(keywordSourceResourceTypeSelector);
        const params = yield select(paramsSelector);
        const accessToken = yield select(accessTokenSelector);
        const options = { accessToken, disableCache: false, retrying: false };

        const keywordsToRefetch = sortedAndFiltered.slice(0, 5).filter(kwObject => kwObject.rank === -1);

        yield put(bulkSilentRefetchAction(keywordsToRefetch.map(kwObject => kwObject.id)));
        yield put(setDashboardBulkFetchProgress(keywordsToRefetch.length, 0));

        for (let i = 0; i < keywordsToRefetch.length; i += 1) {
            const kwObject = keywordsToRefetch[i];

            // get only these that don't have KD yet (so they are not in cache)
            const keywordId = kwObject.id;
            yield delay(800 + Math.round(Math.random() * 400));

            const paramsToSend = {
                query: kwObject.keyword,
                languageId: kwObject.language.id,
                locationId: kwObject.location.id,
                seedLocationId: params.locationId,
            };
            if (sourceType === COMPETITOR) {
                paramsToSend.url = params.query;
            } else if (sourceType === RELATED) {
                paramsToSend.seedKw = params.query;
            }
            const requestOptions = {
                accessToken,
                disableCache: options.disableCache,
                page: 0,
                params: paramsToSend,
            };

            const { result, _timeout } = yield race({
                result: call(SerpBulkResultSource.getData, requestOptions),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error && !isNil(payload)) {
                    yield put(
                        receivedKeywordRankAction(keywordId, payload.rank, payload.rankUpdatedAt, payload.contentTypes),
                    );
                    yield put(
                        receivedKwGapKeywordRankAction(
                            keywordId,
                            payload.rank,
                            payload.rankUpdatedAt,
                            payload.contentTypes,
                        ),
                    );
                    yield put(receivedSingleSearchIntentAction(payload.searchIntent));
                    yield put(receivedSingleSerpFeaturesAction({ [keywordId]: payload.serpFeatures }));
                    yield put(receivedSingleKwOpportunitiesAction({ [keywordId]: payload.kwOpps }));
                } else {
                    yield put(emptyKeywordRankAction(keywordId));
                }
            } else {
                // Timeout
                yield put(emptyKeywordRankAction(keywordId));
                yield call(logError, 'SilentBulkFetchSerpResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }

            yield put(setDashboardBulkFetchProgress(keywordsToRefetch.length, i + 1));
        }
    },
    function* onError() {
        // silent error
    },
);

const bulkFetchSerpResults = handleUncaught(
    function* bulkFetchSerpResults(options = { disableCache: false, retrying: false }) {
        // Check current route to determine if we're in keyword gap mode
        const currentRoute = yield select(currentRouteSelector);
        const isKeywordGap = currentRoute.includes('keyword-gap');

        let selectedKeywordIds, allKeywords;

        if (isKeywordGap) {
            // Use keyword gap selectors
            const selectedKeywords = yield select(filteredAndSortedSelectedKeywordsKeywordGapSelector);
            selectedKeywordIds = selectedKeywords
                .map(kw => kw.id)
                .filter(id => {
                    // Only include keywords that need rank refresh (haven't been updated in 24 hours)
                    const keyword = selectedKeywords.find(kw => kw.id === id);
                    if (!keyword || !keyword.rankUpdatedAt) {
                        return true;
                    }
                    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
                    return keyword.rankUpdatedAt < twentyFourHoursAgo;
                });
            allKeywords = yield select(filteredAndSortedKeywordGapResultsDataSelector);
        } else {
            // Use regular keyword selectors
            selectedKeywordIds = yield select(
                filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursSelector,
            );
            allKeywords = yield select(filteredAndSortedResultsDataSelector);
        }
        const accessToken = yield select(accessTokenSelector);

        let fetchingError = false;

        yield put(setDashboardBulkFetchProgress(selectedKeywordIds.length, 0));

        // NOTE:
        // using plain old `for` because fancy `forEach, maps` etc.
        // are working on functions and I want to `yield` here
        // so it has to be in a context of this generator function.
        for (let i = 0; i < selectedKeywordIds.length; i += 1) {
            const keywordId = selectedKeywordIds[i];

            yield delay(Math.round(Math.random() * 100) + 50);

            const kwObject = find(propEq('id', keywordId))(allKeywords);
            const requestOptions = {
                accessToken,
                disableCache: options.disableCache,
                page: 0,
                params: {
                    query: kwObject.keyword,
                    languageId: kwObject.language.id,
                    locationId: kwObject.location.id,
                },
            };

            const { result, _timeout } = yield race({
                result: call(SerpResultSource.getData, requestOptions),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error && !isNil(payload)) {
                    yield put(
                        receivedKeywordRankAction(keywordId, payload.rank, payload.rankUpdatedAt, payload.contentTypes),
                    );
                    yield put(
                        receivedKwGapKeywordRankAction(
                            keywordId,
                            payload.rank,
                            payload.rankUpdatedAt,
                            payload.contentTypes,
                        ),
                    );
                    yield put(receivedSingleSearchIntentAction(payload.searchIntent));
                    yield put(receivedSingleSerpFeaturesAction({ [keywordId]: payload.serpFeatures }));
                    yield put(receivedSingleKwOpportunitiesAction({ [keywordId]: payload.kwOpps }));
                } else {
                    fetchingError = true;
                    yield put(emptyKeywordRankAction(keywordId));
                }
            } else {
                // Timeout
                yield put(emptyKeywordRankAction(keywordId));
                yield put(errorSerpResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }));
                yield call(logError, 'BulkFetchSerpResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }

            yield put(setDashboardBulkFetchProgress(selectedKeywordIds.length, i + 1));
        }

        if (fetchingError) {
            yield call(showErrorNotification, 'Error while refreshing SERP data!');
        } else {
            yield call(
                showInfoNotification,
                <div>
                    SERP data refresh is <strong>Complete</strong>!
                </div>,
            );
        }
        yield put(requestedLimitsAction());
    },
    function* onError() {
        yield put(errorSerpResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }));
    },
);

const fetchCompetitorsByDomainResults = handleUncaught(
    function* fetchCompetitorsByDomainResults(retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const currentDataParams = yield select(competitorsCurrentDataParamsSelector);
        const competitorsQueryParam = yield select(defaultCompetitorQuerySelector);
        const options = {
            accessToken,
            params: {
                ...params,
                url: competitorsQueryParam,
            },
        };

        if (
            currentDataParams.query === competitorsQueryParam &&
            currentDataParams.locationId === params.locationId &&
            currentDataParams.source === DataSourceTypes.MANGOOLS_COMPETITORS
        ) {
            yield put(skippedCompetitorsByDomainResultsAction());
            return;
        }

        yield put(fetchingCompetitorsByDomainResultsAction());

        const { result, _timeout } = yield race({
            result: call(CompetitorsByDomainResultSource.getData, options),
            _timeout: delay(Defaults.USER_CHECK_INTERVAL), // they guarantee 1 minute
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedCompetitorsByDomainResultsAction(payload));

                if (isNil(payload.data)) {
                    yield put(emptyCompetitorsByDomainResultsAction());
                }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorCompetitorsByDomainResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchCompetitorsByDomainResults, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorCompetitorsByDomainResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        yield put(errorCompetitorsByDomainResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorCompetitorsByDomainResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorCompetitorsByDomainResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchCompetitorsByDomainResultsSaga', payload);
                        } else {
                            yield call(fetchCompetitorsByDomainResults, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorCompetitorsByDomainResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
                            yield call(logError, 'FetchCompetitorsByDomainResultsSaga', payload);
                        } else {
                            yield call(fetchCompetitorsByDomainResults, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorCompetitorsByDomainResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_competitors_results_error }));
            yield call(logError, 'FetchCompetitorsByDomainResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorCompetitorsByDomainResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_competitors_results_error }));
    },
);

const fetchLoadMoreKeywordsByDomainResults = handleUncaught(
    function* fetchLoadMoreKeywordsByDomainResults(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const competitorQuery = yield select(defaultCompetitorQuerySelector);
        const params = yield select(paramsSelector);
        const sortingSettings = yield select(sortingSettingsSelector);
        const currentDataParams = yield select(currentDataParamsSelector);

        const newPage = currentDataParams.page + 1;
        // // Check if data are already fetched (organic/paid switch)
        // if (
        //     currentDataParams.query === competitorQuery &&
        //     newPage === params.page &&
        //     currentDataParams.locationId === params.locationId &&
        //     currentDataParams.source === DataSourceTypes.MANGOOLS_COMPETITORS
        // ) {
        //     yield put(skippedLoadMoreKeywordsByDomainResultsAction());
        //     return;
        // }

        const newParams = {
            ...params,
            page: newPage,
            url: competitorQuery,
            orderBy: sortingSettings.column,
            orderDirection: sortingSettings.type,
        };

        const options = { accessToken, params: newParams };

        yield put(fetchingLoadMoreKeywordsByDomainResultsAction());

        const { result, _timeout } = yield race({
            result: call(KeywordsByDomainResultSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedLoadMoreKeywordsByDomainResultsAction(payload));
                yield all([
                    call(fetchContentType, payload.data),
                    call(fetchSerpFeatures, payload.data),
                    call(fetchSearchIntent, payload.data),
                    call(fetchKwOpportunities, payload.data),
                ]);
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorKeywordsByDomainResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchLoadMoreKeywordsByDomainResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.UNPROCESSABLE_ENTITY: {
                        yield put(errorKeywordsByDomainResultsAction(payload));
                        yield put(
                            showFailureMessage({
                                header: Strings.headers.failure.fetch_results_error_kw_by_domain_422,
                                details: Strings.messages.failure.fetch_results_error_kw_by_domain_422,
                            }),
                        );
                        yield call(logError, 'FetchLoadMoreKeywordsByDomainResultsSaga', payload);
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorKeywordsByDomainResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        yield put(errorKeywordsByDomainResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorKeywordsByDomainResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorKeywordsByDomainResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchLoadMoreKeywordsByDomainResultsSaga', payload);
                        } else {
                            yield call(fetchLoadMoreKeywordsByDomainResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(fetchLoadMoreKeywordsByDomainResults(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
                            yield call(logError, 'FetchLoadMoreKeywordsByDomainResultsSaga', payload);
                        } else {
                            yield call(fetchLoadMoreKeywordsByDomainResults, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorKeywordsByDomainResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_keywords_by_domain_results_error }));
            yield call(logError, 'FetchLoadMoreKeywordsByDomainResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorKeywordsByDomainResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_keywords_by_domain_results_error }));
    },
);

const fetchKeywordsByDomainResults = handleUncaught(
    function* fetchKeywordsByDomainResults(action, retrying = false) {
        const competitorCurrentDataParams = yield select(competitorsCurrentDataParamsSelector);
        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const sortingSettings = yield select(sortingSettingsSelector);
        const competitorQuery = yield select(defaultCompetitorQuerySelector);
        const options = {
            accessToken,
            params: {
                ...params,
                url: competitorQuery,
            },
        };

        // overwrite default sorting prepared for search by KW
        if (sortingSettings.column === 'relevancy') {
            options.params.orderBy = 'serpPosition';
            options.params.orderDirection = 'ASC';
        }

        if (!options.params.orderBy || !options.params.orderDirection) {
            options.params.orderBy = sortingSettings.column || 'serpPosition';
            options.params.orderDirection = sortingSettings.type || 'ASC';
        }

        yield put(setSortingSettings(options.params.orderBy, options.params.orderDirection));

        const subSource = action.payload;
        // Not working good, because after upgrade limit of keywords is checked and user see old data (limited)
        // const currentDataParams = yield select(currentDataParamsSelector);
        // // Check if data are already fetched (organic/paid switch)
        // if (
        //     currentDataParams.query === params.query &&
        //     currentDataParams.orderBy === params.orderBy &&
        //     currentDataParams.orderDirection === params.orderDirection &&
        //     currentDataParams.page === params.page &&
        //     currentDataParams.locationId === params.locationId &&
        //     currentDataParams.source === DataSourceTypes.MANGOOLS_COMPETITORS
        // ) {
        //     yield put(skippedKeywordsByDomainResultsAction());
        //     return;
        // }

        // Not using resetUiStuff, because we need more granular control over ui reset
        if (competitorCurrentDataParams.query !== competitorQuery) {
            yield put(setFilterActive(false));
        }

        yield put(unselectAllKeywords());
        yield put(setQuickFilterSearch(''));
        // yield call(resetUiStuff);

        yield put(fetchingKeywordsByDomainResultsAction());
        yield put(fetchingSerpResultsAction());

        const { result, _timeout } = yield race({
            result: call(KeywordsByDomainResultSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(
                    setKeywordSource({
                        id: null,
                        name: null,
                        type: KeywordSourceTypes.COMPETITOR,
                    }),
                );

                yield put(receivedKeywordsByDomainResultsAction(payload));
                yield all([
                    call(fetchContentType, payload.data),
                    call(fetchSerpFeatures, payload.data),
                    call(fetchSearchIntent, payload.data),
                    call(fetchKwOpportunities, payload.data),
                ]);

                const plan = yield select(userPlanSelector);
                if (plan === 'trial') {
                    yield fork(bulkSilentFetchSerpResults);
                }
                let { keywords } = payload.data;

                // Get first keyword and set params and defaults
                if (subSource === DataSubSourceTypes.COMPETITORS_ORGANIC) {
                    keywords = keywords.filter(item => item.keywordType === KeywordsByDomainTypes.ORGANIC);
                } else if (subSource === DataSubSourceTypes.COMPETITORS_PAID) {
                    keywords = keywords.filter(item => item.keywordType === KeywordsByDomainTypes.PAID);
                }

                // NOTE: Setting location from params, as in ANYWHERE
                // there might be first keyword from other location.
                const { location } = payload.params;
                yield put(setDefaultKeywordsByDomainLocation(location));

                const kw = keywords[0];

                if (!isNil(kw)) {
                    // request competitors data fetch
                    yield fork(fetchCompetitorsByDomainResults, true);
                    yield put(
                        setParams({
                            ...options.params,
                            query: kw.keyword,
                        }),
                    );
                    yield put(setCurrentKeywordId(kw.id));
                    yield put(setDefaultKeywordsByDomainQuery(payload.params.query));

                    // Request serp data fetch
                    yield call(fetchSerpResults, { disableCache: false, retrying: false }, null);
                } else {
                    yield put(emptyKeywordsByDomainResultsAction());
                    yield call(fetchCompetitorsByDomainResults, true);
                }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorKeywordsByDomainResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchKeywordsByDomainResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.UNPROCESSABLE_ENTITY: {
                        yield put(errorKeywordsByDomainResultsAction(payload));
                        yield put(
                            showFailureMessage({
                                header: Strings.headers.failure.fetch_results_error_kw_by_domain_422,
                                details: Strings.messages.failure.fetch_results_error_kw_by_domain_422,
                            }),
                        );
                        yield call(logError, 'FetchKeywordsByDomainResultsSaga', payload);
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorKeywordsByDomainResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        yield put(errorKeywordsByDomainResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorKeywordsByDomainResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorKeywordsByDomainResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchKeywordsByDomainResultsSaga', payload);
                        } else {
                            yield call(fetchKeywordsByDomainResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorKeywordsByDomainResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
                            yield call(logError, 'FetchKeywordsByDomainResultsSaga', payload);
                        } else {
                            yield call(fetchKeywordsByDomainResults, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorKeywordsByDomainResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_keywords_by_domain_results_error }));
            yield call(logError, 'FetchKeywordsByDomainResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorKeywordsByDomainResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_keywords_by_domain_results_error }));
    },
);

const fetchKeywordGapResults = handleUncaught(
    function* fetchKeywordGapResults(retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const currentDataParams = yield select(keywordGapResultsCurrentDataParamsSelector);

        // Extract domain from query and competitors from params
        const domain = params.domain;
        const competitors = params.competitors || [];
        const locationId = params.locationId;
        const mode = params.mode === 'pages' ? 'pages' : 'domains';
        const pages = params.pages || null;
        const excludePages = params.excludePages || null;

        // TODO: 2 fetches happen instead of one because locationId arrives as "0" first then "2840".
        if (
            (currentDataParams.mode || 'domains') === mode &&
            currentDataParams.domain === domain &&
            (currentDataParams.excludePages || null) === excludePages &&
            currentDataParams.locationId === locationId &&
            JSON.stringify(currentDataParams.competitors) === JSON.stringify(competitors) &&
            JSON.stringify(currentDataParams.pages || null) === JSON.stringify(pages) &&
            currentDataParams.page === 1
        ) {
            yield put(skippedKeywordGapResultsAction());
            return;
        }

        const options = {
            accessToken,
            params: {
                mode,
                domain,
                competitors,
                excludePages,
                pages,
                locationId,
                page: 1,
            },
        };

        yield put(fetchingKeywordGapResultsAction());
        const limits = yield select(state => state.user.limits);

        const { result, _timeout } = yield race({
            result: call(KeywordGapResultSource.getData, options),
            _timeout: delay(Defaults.USER_CHECK_INTERVAL),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                let trackingEvent = '';

                if (limits.kwGapMaxCompetitors === 1) {
                    trackingEvent = analyticsEvents.KW_GAP_FREE_RECEIVED_RESULTS;
                } else {
                    if (competitors.length === 1) {
                        trackingEvent = analyticsEvents.KW_GAP_PAID_RECEIVED_RESULTS_SINGLE;
                    } else {
                        trackingEvent = analyticsEvents.KW_GAP_PAID_RECEIVED_RESULTS_MULTI;
                    }
                }
                yield put(
                    gtmTrack({
                        action: analyticsActions.RECEIVE_RESULTS,
                        event: trackingEvent,
                    }),
                );
                yield put(receivedKeywordGapResultsAction(payload));

                yield put(
                    setKeywordSource({
                        id: null,
                        name: null,
                        type: KeywordSourceTypes.KEYWORD_GAP,
                    }),
                );

                const keywords = payload.results.reduce((acc, item) => {
                    return [...acc, ...item.keywords];
                }, []);

                yield all([
                    call(fetchContentType, { keywords }),
                    call(fetchSerpFeatures, { keywords }),
                    call(fetchSearchIntent, { keywords }),
                    call(fetchKwOpportunities, { keywords }),
                ]);

                // if (isNil(payload.data) || payload.data.length === 0) {
                //     yield put(emptyKeywordGapResultsAction());
                // }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorKeywordGapResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchKeywordGapResults, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorKeywordGapResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        yield put(errorKeywordGapResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorKeywordGapResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            if (limits.relatedRemaining === 0) {
                                yield put(showPricingMessage());
                            } else if (options.params.competitors.length > 1 && limits.kwGapMaxCompetitors > 1) {
                                yield put(
                                    showUpsellMessage({
                                        title: 'You don’t have enough keyword lookups for this analysis',
                                        description:
                                            'You have three options: reduce the number of competitors, upgrade or wait for the counters to reset.',
                                        buttonText: 'Upgrade my plan',
                                        buttonLink: queryString.stringifyUrl({
                                            url: Urls.MANGOOLS_PLANS_AND_PRICING_URL,
                                            query: {
                                                ref: 'msg-gap-upsell',
                                                return_url: window.location.href,
                                            },
                                        }),
                                    }),
                                );
                            } else {
                                yield put(showPricingMessage());
                            }
                        }
                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorKeywordGapResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchKeywordGapResultsSaga', payload);
                        } else {
                            yield call(fetchKeywordGapResults, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorKeywordGapResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
                            yield call(logError, 'FetchKeywordGapResultsSaga', payload);
                        } else {
                            yield call(fetchKeywordGapResults, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorKeywordGapResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
            yield call(logError, 'FetchKeywordGapResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorKeywordGapResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
    },
);

const fetchLoadMoreKeywordGapResults = handleUncaught(
    function* fetchLoadMoreKeywordGapResults(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const currentDataParams = yield select(keywordGapResultsCurrentDataParamsSelector);

        const domain = params.domain;
        const competitors = params.competitors || [];
        const locationId = params.locationId;
        // Mirror the mode-aware widening from fetchKeywordGapResults so load-more requests stay
        // shape-consistent if/when the load-more UI is enabled.
        const mode = params.mode === 'pages' ? 'pages' : 'domains';
        const pages = params.pages || null;
        const excludePages = params.excludePages || null;
        const newPage = currentDataParams.page + 1;

        const options = {
            accessToken,
            params: {
                mode,
                domain,
                competitors,
                excludePages,
                pages,
                locationId,
                page: newPage,
            },
        };

        yield put(fetchingLoadMoreKeywordGapResultsAction());

        const { result, _timeout } = yield race({
            result: call(KeywordGapResultSource.getData, options),
            _timeout: delay(Defaults.USER_CHECK_INTERVAL),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedLoadMoreKeywordGapResultsAction(payload));
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorLoadMoreKeywordGapResultsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchLoadMoreKeywordGapResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorLoadMoreKeywordGapResultsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        yield put(errorLoadMoreKeywordGapResultsAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorLoadMoreKeywordGapResultsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorLoadMoreKeywordGapResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchLoadMoreKeywordGapResultsSaga', payload);
                        } else {
                            yield call(fetchLoadMoreKeywordGapResults, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorLoadMoreKeywordGapResultsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
                            yield call(logError, 'FetchLoadMoreKeywordGapResultsSaga', payload);
                        } else {
                            yield call(fetchLoadMoreKeywordGapResults, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorLoadMoreKeywordGapResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
            yield call(logError, 'FetchLoadMoreKeywordGapResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorLoadMoreKeywordGapResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_results_error }));
    },
);

const fetchSerpResultsMore = handleUncaught(
    function* fetchSerpResultsMore(action, retrying = false) {
        yield put(fetchingSerpResultsMoreAction());

        const accessToken = yield select(accessTokenSelector);
        const params = yield select(paramsSelector);
        const page = yield select(nextSerpPageSelector);
        const options = { accessToken, page, params };

        yield put(
            gtmTrack({
                action: analyticsActions.CLICK,
                event: analyticsEvents.LOAD_NEXT_SERP,
                pageNumber: page,
            }),
        );

        const { result, _timeout } = yield race({
            result: call(SerpResultSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        // Hide longer loading notification if this was retry
        // does not matter how it ended up.
        if (retrying === true) {
            yield put(hideLongerSerpLoadingNotification());
        }

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                if (isNil(payload)) {
                    // No content means no SERP results for this keyword
                    yield put(receivedNoMoreSerpResultsAction());
                } else {
                    yield put(receivedSerpResultsMoreAction(payload));
                    yield put(receivedSingleSearchIntentAction(payload.searchIntent));
                    if (payload.page === 0) {
                        yield put(receivedSingleSerpFeaturesAction({ [payload.kwId]: payload.serpFeatures }));
                    }
                }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorSerpResultsMoreAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResultsMore, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorSerpResultsMoreAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.too_many_requests_error }),
                            );
                        } // else if (payload.type === ErrorTypes.CREDIT_LIMIT) { }

                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorSerpResultsMoreAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.REQUEST_TIMEOUT: {
                        if (retrying === true) {
                            yield put(errorSerpResultsMoreAction(payload));
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }),
                            );
                            yield call(logError, 'FetchSerpResultsMoreSaga', payload);
                        } else {
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResultsMore, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorSerpResultsMoreAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchSerpResultsMoreSaga', payload);
                        } else {
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResultsMore, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorSerpResultsMoreAction(payload));
                            yield put(
                                showFailureMessage({
                                    // A 500 carries retry_after only when every SERP provider is
                                    // down; then the wait is real information, not a generic error.
                                    details: isNil(payload.retryAfter)
                                        ? Strings.messages.failure.fetch_serp_results_error
                                        : Strings.messages.failure.serp_unavailable_error.replace(
                                              '{seconds}',
                                              payload.retryAfter,
                                          ),
                                }),
                            );
                            yield call(logError, 'FetchSerpResultsMoreSaga', payload);
                        } else {
                            yield put(showLongerSerpLoadingNotification());
                            yield call(fetchSerpResultsMore, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorSerpResultsMoreAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }));
            yield call(logError, 'FetchSerpResultsMoreSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorSerpResultsMoreAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_serp_results_error }));
    },
);

const fetchLocations = handleUncaught(
    function* fetchLocations(action, retrying = false) {
        yield put(fetchingLocationsAction());
        const accessToken = yield select(accessTokenSelector);
        const query = action.payload;
        const options = { accessToken, query };

        const { result, _timeout } = yield race({
            result: call(LocationSource.getData, options),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedLocationsAction(payload));
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorLocationsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchLocations, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorLocationsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorLocationsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorLocationsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchLocationsSaga', payload);
                        } else {
                            yield call(fetchLocations, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorLocationsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_locations_error }));
                            yield call(logError, 'FetchLocationsSaga', payload);
                        } else {
                            yield call(fetchLocations, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorLocationsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_locations_error }));
            yield call(logError, 'FetchLocationsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorLocationsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_locations_error }));
    },
);

const fetchTrends = handleUncaught(
    function* fetchTrends(action, retrying = false) {
        const currentKeywordGraphType = yield select(defaultSharedCurrentKeywordGraphSelector);

        if (currentKeywordGraphType === KeywordGraphTypes.TRENDS) {
            const accessToken = yield select(accessTokenSelector);
            const keywordObj = yield select(currentKeywordSelector);

            if (!isNil(keywordObj)) {
                const { keyword, location } = keywordObj;
                const meta = yield select(trendMetaDataSelector);

                if (meta.keyword !== keyword || meta.locationId !== location.id) {
                    yield put(fetchingTrendsAction());

                    const { result, _timeout } = yield race({
                        result: call(TrendSource.getData, {
                            accessToken,
                            keyword,
                            locationId: location.id,
                        }),
                        _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
                    });

                    if (!isNil(result)) {
                        const { error, payload } = result;

                        if (!error) {
                            yield put(
                                receivedTrendsAction({ data: payload, meta: { keyword, locationId: location.id } }),
                            );
                        } else {
                            switch (payload.status) {
                                case ErrorCodes.FETCH_ERROR: {
                                    if (retrying === true) {
                                        yield put(errorTrendsAction(payload));
                                        yield put(showNoConnectionMessage());
                                    } else {
                                        // Wait for CONNECTION_RETRY_DELAY and try again
                                        yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                        yield call(fetchTrends, action, true);
                                    }
                                    break;
                                }
                                case ErrorCodes.ACCESS_DENIED: {
                                    yield put(errorTrendsAction(payload));
                                    yield put(showAccessDeniedMessage());
                                    break;
                                }
                                case ErrorCodes.FAILED_DEPENDENCY: {
                                    // NOTE: Proxy captcha
                                    yield put(errorTrendsAction(payload));
                                    break;
                                }
                                case ErrorCodes.TOO_MANY_REQUESTS: {
                                    // Trends are fetched off the back of a search the user already
                                    // made, so a modal here would interrupt them for a chart they
                                    // did not ask to refresh. The empty chart says enough.
                                    yield put(errorTrendsAction(payload));
                                    break;
                                }
                                case ErrorCodes.SERVICE_UNAVAILABLE: {
                                    if (retrying === true) {
                                        yield put(errorTrendsAction(payload));
                                        yield put(
                                            showFailureMessage({ details: Strings.messages.failure.maintenance }),
                                        );
                                        yield call(logError, 'FetchTrendsData', payload);
                                    } else {
                                        yield call(fetchTrends, action, true);
                                    }
                                    break;
                                }
                                case ErrorCodes.INTERNAL_SERVER_ERROR:
                                default: {
                                    if (retrying === true) {
                                        yield put(errorTrendsAction(payload));
                                        yield put(
                                            showFailureMessage({
                                                details: Strings.messages.failure.fetch_trends_error,
                                            }),
                                        );
                                        yield call(logError, 'FetchTrendsData', payload);
                                    } else {
                                        yield call(fetchTrends, action, true);
                                    }
                                    break;
                                }
                            }
                        }
                    } else {
                        // Timeout
                        yield put(errorTrendsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_locations_error }));
                        yield call(logError, 'FetchTrendsData', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
                    }
                } else {
                    // Already cached
                }
            }
        }
    },
    function* onError() {
        yield put(errorTrendsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_locations_error }));
    },
);

const fetchHistoryData = handleUncaught(
    function* fetchHistoryData(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);

        if (!isNil(accessToken)) {
            yield put(fetchingHistoryAction());

            const { result, _timeout } = yield race({
                result: call(HistorySource.getData, accessToken),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedHistoryAction(payload));
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorHistoryAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(fetchHistoryData, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorHistoryAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorHistoryAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorHistoryAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'FetchHistoryDataSaga', payload);
                            } else {
                                yield call(fetchHistoryData, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorHistoryAction(payload));
                                yield put(
                                    showFailureMessage({ details: Strings.messages.failure.fetch_history_error }),
                                );
                                yield call(logError, 'FetchHistoryDataSaga', payload);
                            } else {
                                yield call(fetchHistoryData, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorHistoryAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.fetch_history_error }));
                yield call(logError, 'FetchHistoryDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } else {
            yield put(skippedHistoryAction());
        }
    },
    function* onError() {
        yield put(errorHistoryAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_history_error }));
    },
);

const deleteHistoryData = handleUncaught(function* deleteHistoryData(action, retrying = false) {
    const accessToken = yield select(accessTokenSelector);

    const { result, _timeout } = yield race({
        result: call(HistorySource.delete, { accessToken }),
        _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
    });

    if (!isNil(accessToken)) {
        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedDeleteHistoryAction());

                yield call(showInfoNotification, 'History was successfully cleared.');
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorDeleteHistoryAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(deleteHistoryData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorDeleteHistoryAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorDeleteHistoryAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'DeleteHistorySaga', payload);
                        } else {
                            yield call(deleteHistoryData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorDeleteHistoryAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorDeleteHistoryAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.delete_history }));
                            yield call(logError, 'DeleteHistorySaga', payload);
                        } else {
                            yield call(deleteHistoryData, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorDeleteHistoryAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield call(logError, 'DeleteHistorySaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    }
});

const fetchKdHistoryData = handleUncaught(
    function* fetchKdHistoryData(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);

        if (!isNil(accessToken)) {
            yield put(fetchingKdHistoryAction());

            const { result, _timeout } = yield race({
                result: call(KdHistorySource.getData, accessToken),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedKdHistoryAction(payload));
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorKdHistoryAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(fetchKdHistoryData, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorKdHistoryAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorKdHistoryAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorKdHistoryAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'FetchKdHistoryDataSaga', payload);
                            } else {
                                yield call(fetchKdHistoryData, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorKdHistoryAction(payload));
                                yield put(
                                    showFailureMessage({ details: Strings.messages.failure.fetch_history_error }),
                                );
                                yield call(logError, 'FetchKdHistoryDataSaga', payload);
                            } else {
                                yield call(fetchKdHistoryData, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorKdHistoryAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.fetch_history_error }));
                yield call(logError, 'FetchKdHistoryDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } else {
            yield put(skippedKdHistoryAction());
        }
    },
    function* onError() {
        yield put(errorKdHistoryAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_history_error }));
    },
);

const deleteKdHistoryData = handleUncaught(function* deleteKdHistoryData(action, retrying = false) {
    const accessToken = yield select(accessTokenSelector);

    const { result, _timeout } = yield race({
        result: call(KdHistorySource.delete, { accessToken }),
        _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
    });

    if (!isNil(accessToken)) {
        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedDeleteKdHistoryAction());

                yield call(showInfoNotification, 'History was successfully cleared.');
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorDeleteKdHistoryAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(deleteHistoryData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorDeleteKdHistoryAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorDeleteKdHistoryAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'DeleteKdHistorySaga', payload);
                        } else {
                            yield call(deleteKdHistoryData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorDeleteKdHistoryAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorDeleteKdHistoryAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.delete_history }));
                            yield call(logError, 'DeleteKdHistorySaga', payload);
                        } else {
                            yield call(deleteKdHistoryData, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorDeleteKdHistoryAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield call(logError, 'DeleteKdHistorySaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    }
});

const fetchUrlData = handleUncaught(
    function* fetchUrlData(action, retrying = false) {
        yield put(fetchingUrlDataAction());
        const accessToken = yield select(accessTokenSelector);
        const url = yield select(comparingBoxUrlSelector);
        const protocol = yield select(comparingBoxUrlProtocolSelector);
        const urlWithProtocol = `${protocol.raw}${url}`;

        const { result, _timeout } = yield race({
            result: call(UrlKdDataSource.getData, { accessToken }, { url: urlWithProtocol }),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                if (payload?.linkProfileStrength === 'N/A') {
                    toast.error('We haven’t found any data for your URL.');
                }
                // open dropdown
                yield put(showRelativeKdDropdown());
                yield put(receivedUrlDataAction(payload));
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorUrlDataAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchUrlData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorUrlDataAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNATHORIZED: {
                        yield put(errorUrlDataAction(payload));
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorUrlDataAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.too_many_requests_error }),
                            );
                        } else if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(requestedLimitsAction());
                            yield put(showPricingMessage());
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorUrlDataAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchUrlDataSaga', payload);
                        } else {
                            yield call(fetchUrlData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorUrlDataAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_link_data_error }));
                            yield call(logError, 'FetchUrlDataSaga', payload);
                        } else {
                            yield call(fetchUrlData, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            // Timeout
            yield put(errorUrlDataAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_link_data_error }));
            yield call(logError, 'FetchUrlDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorUrlDataAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_link_data_error }));
    },
);

const fetchListData = handleUncaught(
    function* fetchListData(retrying = false) {
        const accessToken = yield select(accessTokenSelector);

        if (!isNil(accessToken)) {
            yield put(fetchingListsAction());

            const { result, _timeout } = yield race({
                result: call(ListSource.getData, accessToken),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedListsAction(payload));
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorListsAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(fetchListData, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorListsAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorListsAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorListsAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'FetchListsDataSaga', payload);
                            } else {
                                yield call(fetchListData, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorListsAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.fetch_lists_error }));
                                yield call(logError, 'FetchListsDataSaga', payload);
                            } else {
                                yield call(fetchListData, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorListsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield call(logError, 'FetchListsDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } else {
            yield put(skippedListsAction());
        }
    },
    function* onError() {
        yield put(errorListsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
    },
);

const deleteList = handleUncaught(
    function* deleteList(action, retrying = false) {
        const { id, name } = action.payload;
        const accessToken = yield select(accessTokenSelector);

        // Confirmation message helper variables
        let _cancel = null; // eslint-disable-line no-underscore-dangle
        let _closeAll = null; // eslint-disable-line no-underscore-dangle
        let confirm = null;

        if (retrying === false) {
            // Show confirmation message and only proceed if is confirmed
            yield put(showDeleteConfirmationMessage({ resourceName: name, resourceType: DeleteResourceTypes.LIST }));

            ({ _cancel, _closeAll, confirm } = yield race({
                _cancel: take(ActionTypes.UI_MESSAGES_DELETE_CONFIRMATION_CLOSE),
                _closeAll: take(ActionTypes.UI_ALL_CLOSE),
                confirm: take(ActionTypes.UI_MESSAGES_DELETE_CONFIRMATION_CONFIRM),
            }));
        }

        if ((!isNil(confirm) || retrying === true) && !isNil(accessToken)) {
            yield put(fetchingListsDeleteAction());

            const { result, _timeout } = yield race({
                result: call(ListSource.delete, accessToken, id),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    const kwSourceType = yield select(keywordSourceResourceTypeSelector);

                    if (kwSourceType === KeywordSourceTypes.LIST) {
                        const kwSourceResourceId = yield select(keywordSourceResourceIdSelector);

                        if (kwSourceResourceId === id) {
                            // Deleting currently opened list, redirect to root
                            yield put(requestedNavigationAction(RoutePaths.ROOT, {}));
                        }
                    }

                    yield put(receivedListsDeleteAction(id));
                    yield call(
                        showInfoNotification,
                        <div>
                            List <strong>{name}</strong> was successfully deleted.
                        </div>,
                    );
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorListsDeleteAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(deleteList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorListsDeleteAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorListsDeleteAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorListsDeleteAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'DeleteListSaga', payload);
                            } else {
                                yield call(deleteList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorListsDeleteAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.delete_list_error }));
                                yield call(logError, 'DeleteListSaga', payload);
                            } else {
                                yield call(deleteList, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorListsDeleteAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.delete_list_error }));
                yield call(logError, 'DeleteListSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } else {
            yield put(cancelledListsDeleteAction(id));
        }
    },
    function* onError() {
        yield put(errorListsDeleteAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.delete_list_error }));
    },
);

const renameList = handleUncaught(
    function* renameList(action, retrying = false) {
        const { id, name } = action.payload;
        const accessToken = yield select(accessTokenSelector);

        if (!isNil(accessToken)) {
            yield put(fetchingListsRenameAction());

            const { result, _timeout } = yield race({
                result: call(ListSource.rename, accessToken, name, id),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedListsRenameAction(id, name));
                    yield call(showInfoNotification, <ListUpdatedAlert listName={name} />, {
                        html: true,
                    });
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorListsRenameAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(renameList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorListsRenameAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorListsRenameAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorListsRenameAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'RenameListSaga', payload);
                            } else {
                                yield call(renameList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorListsRenameAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.rename_list_error }));
                                yield call(logError, 'RenameListSaga', payload);
                            } else {
                                yield call(renameList, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorListsRenameAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.rename_list_error }));
                yield call(logError, 'RenameListSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        }
    },
    function* onError() {
        yield put(errorListsRenameAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.rename_list_error }));
    },
);

const exportSelectedKeywordsData = handleUncaught(
    function* exportSelectedKeywordsData(action) {
        yield put(setExporting(true));

        let sortedKeywords = [];
        if (action.payload.type === 'keyword-gap') {
            sortedKeywords = yield select(filteredAndSortedSelectedKeywordsKeywordGapSelector);
        } else {
            sortedKeywords = yield select(filteredAndSortedSelectedKeywordsSelector);
        }

        const kwSourceType = yield select(keywordSourceResourceTypeSelector);
        const stringForFilename = yield select(exportSelectedKeywordsFilenameSelector);
        const currency = yield select(currencySelector);
        const urlDataLps = yield select(urlDataLpsSelector);
        const relativeKdActive = yield select(relativeKdActiveSelector);

        // CSV header for kw-gap exports needs the user's own target (domain or pages-mode own URL)
        // so the column reads `SERP Position - {target}` instead of the legacy `Your domain` literal.
        let ownTarget;
        if (action.payload.type === 'keyword-gap') {
            const params = yield select(paramsSelector);
            ownTarget = params && params.mode === 'pages'
                ? params.excludePages
                : (params ? params.domain : undefined);
        }

        const filename = `${EXPORT_PREFIX}${FileService.sanitizeStringForFilename(stringForFilename)}${EXPORT_SUFFIX}`;

        const csv = yield call(ExportService.exportKeywords, sortedKeywords, {
            kwSourceType,
            currency,
            urlDataLps,
            relativeKdActive,
            ownTarget,
        });

        const success = yield call(DownloaderService.downloadCSV, filename, csv);

        if (success) {
            yield call(showInfoNotification, 'Your keywords were successfully exported.');
            yield put(setExporting(false));
        } else {
            yield put(showFailureMessage({ details: Strings.messages.failure.download_error }));
            yield call(logError, 'ExportSelectedKeywordsDataSaga|DownloaderService', action.payload);
            yield put(setExporting(false));
        }
    },
    function* onError() {
        yield put(showFailureMessage({ details: Strings.messages.failure.export_error }));
        yield put(setExporting(false));
    },
);

function* revertUpdateListsAddKeywordsSaga(list) {
    yield put(
        revertUpdateListsAddKeywords({
            listId: list.id,
            keywordIds: list.keywordIds,
            updatedAt: list.updatedAt,
        }),
    );
}

const addOrCreateList = handleUncaught(
    function* addOrCreateList(action, retrying = false, prevList = null) {
        const { isNew, nameOrId } = action.payload;
        const keywordIds = yield select(addToListMessageAddingKeywordIdsSelector);
        const accessToken = yield select(accessTokenSelector);

        if (isNew === true) {
            yield put(fetchingListsAction());

            const { result, _timeout } = yield race({
                result: call(ListSource.create, accessToken, keywordIds, nameOrId),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedListsNewAction(payload));
                    yield call(showInfoNotification, <ListCreatedAlert listName={nameOrId} />, {
                        html: true,
                    });
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorListsCreateAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(addOrCreateList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(errorListsCreateAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(errorListsCreateAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorListsCreateAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'AddOrCreateListDataSaga', payload);
                            } else {
                                yield call(addOrCreateList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorListsCreateAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.create_list_error }));
                                yield call(logError, 'AddOrCreateListDataSaga', payload);
                            } else {
                                yield call(addOrCreateList, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(errorListsCreateAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.create_list_error }));
                yield call(logError, 'AddOrCreateListDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } else if (isNew === false) {
            yield put(fetchingListsAction());
            const currentList = yield select(listSelector, nameOrId);

            if (retrying === false) {
                yield put(updateListsAddKeywords({ listId: nameOrId, keywordIds }));

                const keywordCount = keywordIds.length;
                const label = keywordCount > 1 ? 'keywords were' : 'keyword was';

                yield call(
                    showInfoNotification,
                    <div>
                        {keywordCount} {label} successfully added to <strong>{currentList.name}</strong>.
                    </div>,
                );
            }

            const { result, _timeout } = yield race({
                result: call(ListSource.addKeywords, { accessToken, keywordIds, listId: nameOrId }),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    yield put(receivedListsAddKeywordsAction());
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield call(revertUpdateListsAddKeywordsSaga, prevList);
                                yield put(errorListsAddKeywordsAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(addOrCreateList, action, true, currentList);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield call(revertUpdateListsAddKeywordsSaga, currentList);
                            yield put(errorListsAddKeywordsAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield call(revertUpdateListsAddKeywordsSaga, currentList);
                            yield put(errorListsAddKeywordsAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.UNPROCESSABLE_ENTITY: {
                            yield call(revertUpdateListsAddKeywordsSaga, currentList);
                            yield put(errorListsAddKeywordsAction(payload));
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.update_list_limit_error }),
                            );
                            yield call(logError, 'AddOrCreateListDataSaga', payload);
                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield call(revertUpdateListsAddKeywordsSaga, prevList);
                                yield put(errorListsAddKeywordsAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'AddOrCreateListDataSaga', payload);
                            } else {
                                yield call(addOrCreateList, action, true, currentList);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield call(revertUpdateListsAddKeywordsSaga, prevList);
                                yield put(errorListsAddKeywordsAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.update_list_error }));
                                yield call(logError, 'AddOrCreateListDataSaga', payload);
                            } else {
                                yield call(addOrCreateList, action, true, currentList);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield call(revertUpdateListsAddKeywordsSaga, currentList);
                yield put(errorListsAddKeywordsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.update_list_error }));
                yield call(logError, 'AddOrCreateListDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        }
    },
    function* onError() {
        yield put(errorListsAddKeywordsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.update_list_error }));
    },
);

const loadDashboardWithListData = handleUncaught(
    function* loadDashboardWithListData(action, retrying = false) {
        const listId = action.payload;

        yield call(resetUiStuff);
        yield put(fetchingListKeywordsAction());

        const accessToken = yield select(accessTokenSelector);

        const { result, _timeout } = yield race({
            result: call(ListSource.getDetail, accessToken, listId),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                const { list, keywords } = payload;

                // Set list as kw source and also the name of the list
                yield put(
                    setKeywordSource({
                        id: list.id,
                        name: list.name,
                        type: KeywordSourceTypes.LIST,
                    }),
                );

                // Return list keywords
                yield put(receivedListKeywordsAction(keywords));

                // Get first keyword and set params
                const kw = keywords[0];
                yield put(
                    setParams({
                        query: kw.keyword,
                        languageId: kw.language.id,
                        locationId: kw.location.id,
                        page: 1,
                    }),
                );

                // Request serp data fetch
                yield call(fetchSerpResults, { disableCache: false, retrying: false }, null);

                // Request search intent data
                yield call(fetchSearchIntent, payload);

                // Request kw opportunities data
                yield call(fetchKwOpportunities, payload);

                // Request content type data
                yield call(fetchContentType, payload);

                // Request serp features data
                yield call(fetchSerpFeatures, payload);
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorListKeywordsAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(loadDashboardWithListData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorListKeywordsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.UNPROCESSABLE_ENTITY: {
                        yield put(errorListKeywordsAction(payload));
                        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_list_detail_error }));
                        yield call(logError, 'LoadDashboardWithListsDataSaga', payload);
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorListKeywordsAction(payload));

                        if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.too_many_requests_error,
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorListKeywordsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'LoadDashboardWithListsDataSaga', payload);
                        } else {
                            yield call(loadDashboardWithListData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorListKeywordsAction(payload));
                            yield put(
                                showFailureMessage({ details: Strings.messages.failure.fetch_list_detail_error }),
                            );
                            yield call(logError, 'LoadDashboardWithListsDataSaga', payload);
                        } else {
                            yield call(loadDashboardWithListData, action, true);
                        }
                        break;
                    }
                }

                yield call(logError, 'LoadDashboardWithListsDataSaga', payload);
            }
        } else {
            yield put(errorListKeywordsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_list_detail_error }));
            yield call(logError, 'LoadDashboardWithListsDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorListKeywordsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_list_detail_error }));
    },
);

const importCurrentKeywords = handleUncaught(function* importCurrentKeywords() {
    const keywordData = yield select(resultsDataSelector);
    const keywords = keywordData.map(kw => kw.keyword);

    const location = yield select(defaultGoogleLocationSelector);
    const locationId = location.id;

    yield put(requestedKeywordsImportAction(keywords, locationId));
});

const loadDashboardWithImportedData = handleUncaught(
    function* loadDashboardWithImportedData(action, retrying = false) {
        const { keywords, locationId } = action.payload;
        yield put(requestedNavigationAction(RoutePaths.DASHBOARD, { importId: Date.now() }));

        yield call(resetUiStuff);
        yield put(fetchingImportAction());

        const accessToken = yield select(accessTokenSelector);

        const { result, _timeout } = yield race({
            result: call(ImportSource.getData, accessToken, { keywords, locationId }),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;
            const { data, meta } = payload;

            if (!error) {
                if (!isEmpty(data)) {
                    // Set import as kw source
                    yield put(
                        setKeywordSource({
                            id: null,
                            name: null,
                            type: KeywordSourceTypes.IMPORT,
                        }),
                    );

                    // Return received keywords
                    yield put(receivedImportAction({ data, meta }));

                    // Get first keyword and set params
                    const kw = data[0];
                    yield put(
                        setParams({
                            query: kw.keyword,
                            languageId: kw.language.id,
                            locationId: kw.location.id,
                            page: 1,
                        }),
                    );
                    yield put(setDefaultGoogleLocation(kw.location));

                    // Request serp data fetch
                    yield call(fetchSerpResults, { disableCache: false, retrying: false }, null);

                    // Request search intent data
                    yield call(fetchSearchIntent, { keywords: payload.data });

                    // Request kw opportunities data
                    yield call(fetchKwOpportunities, { keywords: payload.data });

                    // Request content type data
                    yield call(fetchContentType, { keywords: payload.data });

                    // Request serp features data
                    yield call(fetchSerpFeatures, { keywords: payload.data });
                } else {
                    // No keywords were returned from import, show import error
                    yield put(errorImportAction(EMPTY_IMPORT_ERROR));
                    yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
                    yield call(logError, 'LoadDashboardWithImportedDataSaga', EMPTY_IMPORT_ERROR);
                }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorImportAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(loadDashboardWithImportedData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.FAILED_DEPENDENCY: {
                        yield put(errorImportAction({ ...payload, lastPayload: action.payload }));
                        yield put(
                            showFailureMessage({
                                details: Strings.messages.failure.import_error_424,
                            }),
                        );
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorImportAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorImportAction(payload));

                        // CreditLimit means the plan is spent, RateLimit means too many requests.
                        // Until the API split is deployed a spent plan still answers RateLimit and
                        // lands in the wait branch below - the accepted cost of shipping the front
                        // end first, see the deploy-order note in the pull request.
                        if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        } else if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    header: Strings.messages.failure.import_cooldown_header,
                                    details: isNil(payload.retryAfter)
                                        ? Strings.messages.failure.too_many_requests_error
                                        : Strings.messages.failure.import_cooldown_error.replace(
                                              '{seconds}',
                                              payload.retryAfter,
                                          ),
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorImportAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'LoadDashboardWithImportedDataSaga', payload);
                        } else {
                            yield call(loadDashboardWithImportedData, action, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorImportAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
                            yield call(logError, 'LoadDashboardWithImportedDataSaga', payload);
                        } else {
                            yield call(loadDashboardWithImportedData, action, true);
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorImportAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
            yield call(logError, 'LoadDashboardWithImportedDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorImportAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
    },
);

const loadDashboardWithImportedAutocompleteData = handleUncaught(
    function* loadDashboardWithImportedAutocompleteData(
        { keywords, languageId, locationId, subSourceType },
        retrying = false,
    ) {
        yield call(resetUiStuff);
        yield put(fetchingImportAction());

        const accessToken = yield select(accessTokenSelector);

        const { result, _timeout } = yield race({
            result: call(ImportSource.getData, accessToken, { keywords, languageId, locationId }),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;
            const { data, meta } = payload;

            if (!error) {
                if (!isEmpty(data)) {
                    // Setting autocomplete/questions as kw source type
                    if (subSourceType === DataSubSourceTypes.GOOGLE_AUTOCOMPLETE) {
                        yield put(
                            setKeywordSource({
                                id: null,
                                name: null,
                                type: KeywordSourceTypes.AUTOCOMPLETE,
                            }),
                        );
                    } else if (subSourceType === DataSubSourceTypes.GOOGLE_QUESTIONS) {
                        yield put(
                            setKeywordSource({
                                id: null,
                                name: null,
                                type: KeywordSourceTypes.QUESTIONS,
                            }),
                        );
                    }

                    // Return received keywords
                    yield put(finishedGoogleResultsAction());
                    yield put(receivedImportAction({ data, meta }));

                    // Get first keyword and set params and defaults
                    const kw = data[0];
                    yield put(
                        setParams({
                            query: kw.keyword,
                            languageId: kw.language.id,
                            locationId: kw.location.id,
                            page: 1,
                        }),
                    );
                    yield put(setDefaultGoogleQuery(kw.keyword));
                    yield put(setDefaultGoogleLanguage(kw.language));
                    yield put(setDefaultGoogleLocation(kw.location));

                    // Request serp data fetch
                    yield call(fetchSerpResults, { disableCache: false, retrying: false }, null);

                    // Request search intent data
                    yield call(fetchSearchIntent, { keywords: payload.data });

                    // Request kw opportunities data
                    yield call(fetchKwOpportunities, { keywords: payload.data });

                    // Request content type data
                    yield call(fetchContentType, { keywords: payload.data });

                    // Request serp features data
                    yield call(fetchSerpFeatures, { keywords: payload.data });
                } else {
                    // No keywords were returned from import, show import error
                    yield put(errorImportAction(EMPTY_IMPORT_ERROR));
                    yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
                    yield call(logError, 'LoadDashboardWithImportedAutocompleteDataSaga', EMPTY_IMPORT_ERROR);
                }
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorImportAction(payload));
                            yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(
                                loadDashboardWithImportedAutocompleteData,
                                {
                                    keywords,
                                    languageId,
                                    locationId,
                                    subSourceType,
                                },
                                true,
                            );
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorImportAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.FAILED_DEPENDENCY: {
                        yield put(errorImportAction(payload));
                        yield put(
                            showFailureMessage({
                                details: Strings.messages.failure.import_error_424,
                            }),
                        );
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        yield put(errorImportAction(payload));

                        // CreditLimit means the plan is spent, RateLimit means too many requests.
                        // Until the API split is deployed a spent plan still answers RateLimit and
                        // lands in the wait branch below - the accepted cost of shipping the front
                        // end first, see the deploy-order note in the pull request.
                        if (payload.type === ErrorTypes.CREDIT_LIMIT) {
                            yield put(showPricingMessage());
                        } else if (payload.type === ErrorTypes.RATE_LIMIT) {
                            yield put(
                                showFailureMessage({
                                    header: Strings.messages.failure.import_cooldown_header,
                                    details: isNil(payload.retryAfter)
                                        ? Strings.messages.failure.too_many_requests_error
                                        : Strings.messages.failure.import_cooldown_error.replace(
                                              '{seconds}',
                                              payload.retryAfter,
                                          ),
                                }),
                            );
                        }

                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorImportAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'LoadDashboardWithImportedAutocompleteDataSaga', payload);
                        } else {
                            yield call(
                                loadDashboardWithImportedAutocompleteData,
                                {
                                    keywords,
                                    languageId,
                                    locationId,
                                    subSourceType,
                                },
                                true,
                            );
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorImportAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
                            yield call(logError, 'LoadDashboardWithImportedAutocompleteDataSaga', payload);
                        } else {
                            yield call(
                                loadDashboardWithImportedAutocompleteData,
                                {
                                    keywords,
                                    languageId,
                                    locationId,
                                    subSourceType,
                                },
                                true,
                            );
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorImportAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
            yield call(logError, 'LoadDashboardWithImportedAutocompleteDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorImportAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.import_error }));
    },
);

// NOTE: Not retryable
function* fetchAggregatedGoogleResults(subSourceType, { keyword, limit, languageCode, locationCode }) {
    const result = { error: false, payload: [] };
    let generatedKeywords = [];

    if (subSourceType === DataSubSourceTypes.GOOGLE_AUTOCOMPLETE) {
        generatedKeywords = yield call(generateKeywords, keyword, languageCode);
    } else if (subSourceType === DataSubSourceTypes.GOOGLE_QUESTIONS) {
        generatedKeywords = yield call(generateQuestions, keyword, languageCode);
    }

    const requestCount = generatedKeywords.length;
    yield put(setDashboardLeftLoaderProgress(requestCount, 0));

    // NOTE:
    // using plain old `for` because fancy `forEach, maps` etc.
    // are working on functions and I want to `yield` here
    // so it has to be in a context of this generator function.
    for (let i = 0; i < generatedKeywords.length; i += 1) {
        const nextKeyword = generatedKeywords[i];
        if (result.payload.length < limit) {
            // Wait a random time between 50 and 150ms for
            // less suspicious requests to google search API
            yield delay(Math.round(Math.random() * 100) + 50);

            // Get data for next keyword
            const { error, payload } = yield call(GoogleSource.getData, {
                keyword: nextKeyword,
                languageCode,
                locationCode,
            });

            if (error === false) {
                // Filter URLs, concat, uniq returned autocomplete keywords
                const filteredPayload = payload.filter(kw => !isURL(kw));
                result.payload = concat(result.payload, filteredPayload);
                result.payload = uniq(result.payload);

                // Update finished state for loading indicator
                yield put(setDashboardLeftLoaderProgress(requestCount, i + 1));
            } else {
                result.error = true;
                result.payload = payload;
                break;
            }
        }
    }

    if (result.error === false) {
        // This should be set in last iteration, but when we hit `limit`
        // we dont have to do more requests. So jump to the end.
        yield put(setDashboardLeftLoaderProgress(requestCount, requestCount));

        if (result.payload.length > 0) {
            // We have at least one autocompleted keyword
            // so add seed keyword at the top and return
            result.payload = concat([keyword], result.payload);
        }

        // Slice according to limits
        result.payload = slice(0, limit, result.payload);

        return result;
    } else {
        // Return error payload
        return result;
    }
}

// NOTE: Not retryable
const fetchGoogleResults = handleUncaught(
    function* fetchGoogleResults(subSourceType) {
        yield put(fetchingGoogleResultsAction());
        const limitsFetched = yield select(limitsFetchedSelector);
        const loggedIn = yield select(loggedInSelector);

        if (loggedIn === true && limitsFetched === false) {
            // Wait for import limit to be determined
            yield take(ActionTypes.DATA_USER_LIMIT_DATA_RECEIVED);
        }

        const language = yield select(defaultGoogleLanguageSelector);
        const limit = yield select(importLimitSelector);
        const params = yield select(paramsSelector);

        // NOTE: When using location select, defaultLocation will be the correct location.
        // When navigating via link/form then the locationId from params will differ
        // from defaultLocation. So we fetch this new location and save it as default.
        const defaultLocation = yield select(defaultGoogleLocationSelector);
        let location = defaultLocation;

        if (defaultLocation.id !== params.locationId) {
            const accessToken = yield select(accessTokenSelector);

            const { result, _timeout } = yield race({
                result: call(LocationSource.getById, { accessToken, id: params.locationId }),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    location = payload;
                    yield put(setDefaultGoogleLocation(payload));
                }
            }
        }

        // GoogleSource options
        const options = {
            keyword: params.query,
            languageCode: language.code,
            limit,
            locationCode: location.code,
        };

        const { result, timeout } = yield race({
            result: call(fetchAggregatedGoogleResults, subSourceType, options),
            timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 3),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (error === true) {
                yield put(errorGoogleResultsAction(payload));

                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        yield put(showNoConnectionMessage());
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        yield put(
                            showFailureMessage({
                                header: Strings.headers.failure.fetch_google_results_error,
                                details: Strings.messages.failure.fetch_google_results_error,
                            }),
                        );
                        break;
                    }
                }

                yield call(logError, 'FetchGoogleResultsSaga', payload);
            } else if (error === false && !isEmpty(payload)) {
                yield call(
                    loadDashboardWithImportedAutocompleteData,
                    {
                        keywords: payload,
                        languageId: language.id,
                        locationId: location.id,
                        subSourceType,
                    },
                    false,
                );
            } else if (isEmpty(payload)) {
                yield put(emptyGoogleResultsAction());
            }
        } else if (!isNil(timeout)) {
            // Timeout
            yield put(errorGoogleResultsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield put(showFailureMessage({ details: Strings.messages.failure.fetch_google_results_timeout_error }));
            yield call(logError, 'FetchGoogleResultsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorGoogleResultsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_google_results_error }));
    },
);

function* fetchGoogleAutocompleteResults() {
    yield call(fetchGoogleResults, DataSubSourceTypes.GOOGLE_AUTOCOMPLETE);
}

function* fetchGoogleQuestionsResults() {
    yield call(fetchGoogleResults, DataSubSourceTypes.GOOGLE_QUESTIONS);
}

const deleteKeywordFromList = handleUncaught(
    function* deleteKeywordFromList(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const { keywordId, listId } = action.payload;
        const currentListId = yield select(keywordSourceResourceIdSelector);

        const isCurrentList = !isNil(currentListId) && currentListId === listId;

        if (!retrying) {
            if (!isCurrentList) {
                yield put(optimisticDeleteKeywordFromListAction({ listId, keywordId }));
            } else {
                yield put(optimisticDeleteKeywordsFromListAction({ listId, keywordIds: [keywordId] }));
            }

            yield call(showInfoNotification, 'Keyword was successfully removed from list');
        }

        const { result, _timeout } = yield race({
            result: call(ListSource.deleteKeywords, { accessToken, keywordIds: [keywordId], listId }),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
        });

        if (!isNil(accessToken)) {
            if (!isNil(result)) {
                const { error, payload } = result;

                if (error) {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(showNoConnectionMessage());
                                if (!isCurrentList) {
                                    yield put(revertOptimisticDeleteKeywordFromListAction());
                                } else {
                                    yield put(revertOptimisticDeleteKeywordsFromListAction());
                                }
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(deleteKeywordFromList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(showAccessDeniedMessage());
                            if (!isCurrentList) {
                                yield put(revertOptimisticDeleteKeywordFromListAction());
                            } else {
                                yield put(revertOptimisticDeleteKeywordsFromListAction());
                            }
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            if (!isCurrentList) {
                                yield put(revertOptimisticDeleteKeywordFromListAction());
                            } else {
                                yield put(revertOptimisticDeleteKeywordsFromListAction());
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'DeleteKeywordFromListSaga', payload);
                                if (!isCurrentList) {
                                    yield put(revertOptimisticDeleteKeywordFromListAction());
                                } else {
                                    yield put(revertOptimisticDeleteKeywordsFromListAction());
                                }
                            } else {
                                yield call(deleteKeywordFromList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.delete_keywords_from_list_error,
                                    }),
                                );
                                if (!isCurrentList) {
                                    yield put(revertOptimisticDeleteKeywordFromListAction());
                                } else {
                                    yield put(revertOptimisticDeleteKeywordsFromListAction());
                                }
                                yield call(logError, 'DeleteKeywordFromListSaga', payload);
                            } else {
                                yield call(deleteKeywordFromList, action, true);
                            }
                        }
                    }
                }
            } else {
                if (!isCurrentList) {
                    yield put(revertOptimisticDeleteKeywordFromListAction());
                } else {
                    yield put(revertOptimisticDeleteKeywordsFromListAction());
                }
                yield put(showFailureMessage({ details: Strings.messages.failure.delete_keywords_from_list_error }));
                yield call(logError, 'DeleteKeywordsFromCurrentListSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        }
    },
    function* onError() {
        yield put(revertOptimisticDeleteKeywordFromListAction());
        yield put(revertOptimisticDeleteKeywordsFromListAction());
        yield put(showFailureMessage({ details: Strings.messages.failure.delete_keyword_from_list_error }));
    },
);
const deleteKeywordsFromCurrentList = handleUncaught(
    function* deleteKeywordsFromCurrentList(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);
        const selectedKeywordIds = yield select(filteredAndSortedSelectedKeywordIdsSelector);
        const newCurrentKwId = yield select(newCurrentKwIdSelector);
        const selectedKeywordCount = selectedKeywordIds.length;
        const list = yield select(currentListDetailDataSelector);

        // Confirmation message helper variables
        let _cancel = null; // eslint-disable-line no-underscore-dangle
        let _closeAll = null; // eslint-disable-line no-underscore-dangle
        let confirm = null;

        if (retrying === false) {
            // Show confirmation message and only proceed if is confirmed
            yield put(
                showDeleteConfirmationMessage({
                    resourceName: selectedKeywordCount.toString(),
                    resourceType: DeleteResourceTypes.SELECTED_KEYWORDS,
                }),
            );

            ({ _cancel, _closeAll, confirm } = yield race({
                _cancel: take(ActionTypes.UI_MESSAGES_DELETE_CONFIRMATION_CLOSE),
                _closeAll: take(ActionTypes.UI_ALL_CLOSE),
                confirm: take(ActionTypes.UI_MESSAGES_DELETE_CONFIRMATION_CONFIRM),
            }));
        }

        if (!isNil(confirm) && !retrying) {
            // NOTE: Not deactivating filter, sorting setting, just resetting selections
            yield put(unselectAllKeywords());

            // Handle empty list and deleted current kw
            if (list.keywordIds.length === selectedKeywordIds.length) {
                // Deleted all kws, redirect to root
                yield put(requestedNavigationAction(RoutePaths.ROOT, {}));
            }

            yield put(optimisticDeleteKeywordsFromListAction({ listId: list.id, keywordIds: selectedKeywordIds }));

            // Show notification
            const label = selectedKeywordCount > 1 ? 'keywords were' : 'keyword was';

            yield call(showInfoNotification, `${selectedKeywordCount} ${label} successfully removed`);

            yield put(setCurrentKeywordId(newCurrentKwId));
        }

        if (!isNil(confirm) || retrying === true) {
            const { result, _timeout } = yield race({
                result: call(ListSource.deleteKeywords, {
                    accessToken,
                    keywordIds: selectedKeywordIds,
                    listId: list.id,
                }),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT * 2),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (error) {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(revertOptimisticDeleteKeywordsFromListAction());
                                yield put(errorDeleteSelectedKeywordsFromCurrentListAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(deleteKeywordsFromCurrentList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.ACCESS_DENIED: {
                            yield put(revertOptimisticDeleteKeywordsFromListAction());
                            yield put(errorDeleteSelectedKeywordsFromCurrentListAction(payload));
                            yield put(showAccessDeniedMessage());
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            yield put(revertOptimisticDeleteKeywordsFromListAction());
                            yield put(errorDeleteSelectedKeywordsFromCurrentListAction(payload));

                            if (payload.type === ErrorTypes.RATE_LIMIT) {
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.too_many_requests_error,
                                    }),
                                );
                            }

                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(revertOptimisticDeleteKeywordsFromListAction());
                                yield put(errorDeleteSelectedKeywordsFromCurrentListAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'DeleteKeywordsFromCurrentListSaga', payload);
                            } else {
                                yield call(deleteKeywordsFromCurrentList, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(revertOptimisticDeleteKeywordsFromListAction());
                                yield put(errorDeleteSelectedKeywordsFromCurrentListAction(payload));
                                yield put(
                                    showFailureMessage({
                                        details: Strings.messages.failure.delete_keywords_from_list_error,
                                    }),
                                );
                                yield call(logError, 'DeleteKeywordsFromCurrentListSaga', payload);
                            } else {
                                yield call(deleteKeywordsFromCurrentList, action, true);
                            }
                            break;
                        }
                    }
                }
            } else {
                yield put(revertOptimisticDeleteKeywordsFromListAction());
                yield put(errorDeleteSelectedKeywordsFromCurrentListAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
                yield put(showFailureMessage({ details: Strings.messages.failure.delete_keywords_from_list_error }));
                yield call(logError, 'DeleteKeywordsFromCurrentListSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
            }
        } else {
            yield put(cancelledDeleteSelectedKeywordsFromCurrentListAction());
        }
    },
    function* onError() {
        yield put(revertOptimisticDeleteKeywordsFromListAction());
        yield put(errorDeleteSelectedKeywordsFromCurrentListAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.delete_keywords_from_list_error }));
    },
);

const fetchAnnouncements = handleUncaught(
    function* fetchAnnouncements(retrying = false) {
        yield put(fetchingAnnouncementsAction());

        const { result, _timeout } = yield race({
            result: call(AnnouncementsSource.getData),
            _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
        });

        if (!isNil(result)) {
            const { error, payload } = result;

            if (!error) {
                yield put(receivedAnnouncementsAction(payload));
            } else {
                switch (payload.status) {
                    case ErrorCodes.FETCH_ERROR: {
                        if (retrying === true) {
                            yield put(errorAnnouncementsAction(payload));
                            // yield put(showNoConnectionMessage());
                        } else {
                            // Wait for CONNECTION_RETRY_DELAY and try again
                            yield delay(Defaults.CONNECTION_RETRY_DELAY);
                            yield call(fetchAnnouncements, true);
                        }
                        break;
                    }
                    case ErrorCodes.ACCESS_DENIED: {
                        yield put(errorAnnouncementsAction(payload));
                        yield put(showAccessDeniedMessage());
                        break;
                    }
                    case ErrorCodes.TOO_MANY_REQUESTS: {
                        // Background poll - it must never put a modal in front of the user
                        yield put(errorAnnouncementsAction(payload));
                        break;
                    }
                    case ErrorCodes.SERVICE_UNAVAILABLE: {
                        if (retrying === true) {
                            yield put(errorAnnouncementsAction(payload));
                            yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                            yield call(logError, 'FetchAnnouncementsSaga', payload);
                        } else {
                            yield call(fetchAnnouncements, true);
                        }
                        break;
                    }
                    case ErrorCodes.INTERNAL_SERVER_ERROR:
                    default: {
                        if (retrying === true) {
                            yield put(errorAnnouncementsAction(payload));
                            yield put(
                                showFailureMessage({
                                    details: Strings.messages.failure.fetch_announcements_error,
                                }),
                            );
                            yield call(logError, 'FetchAnnouncementsSaga', payload);
                        } else {
                            yield call(fetchAnnouncements, true);
                        }
                        break;
                    }
                }
            }
        } else {
            yield put(errorAnnouncementsAction(INTERNAL_TIMEOUT_ERROR_PAYLOAD));
            yield call(logError, 'FetchAnnouncementsSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
        }
    },
    function* onError() {
        yield put(errorAnnouncementsAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
    },
);

const checkUnleashSession = handleUncaught(function* checkUnleashSession(action, retrying = false) {
    const { result, _timeout } = yield race({
        result: call(UnleashSource.get),
        _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
    });

    if (!isNil(result)) {
        const { error, payload } = result;

        if (!error) {
            yield put(setUnleashSessionAction({ unleashSession: payload.sessionId }));
        } else {
            switch (payload.status) {
                case ErrorCodes.FETCH_ERROR: {
                    if (retrying !== true) {
                        // Wait for CONNECTION_RETRY_DELAY and try again
                        yield delay(Defaults.CONNECTION_RETRY_DELAY);
                        yield call(checkUnleashSession, action, true);
                    }
                    break;
                }
                case ErrorCodes.SERVICE_UNAVAILABLE: {
                    if (retrying === true) {
                        yield call(logError, 'FetchUnleashSessionDataSaga', payload);
                    } else {
                        yield call(checkUnleashSession, action, true);
                    }
                    break;
                }
                case ErrorCodes.INTERNAL_SERVER_ERROR:
                default: {
                    if (retrying === true) {
                        yield call(logError, 'FetchUnleashSessionDataSaga', payload);
                    } else {
                        yield call(checkUnleashSession, action, true);
                    }
                    break;
                }
            }
        }
    } else {
        yield call(logError, 'FetchUnleashSessionDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
    }
});

// NOTE:
//
// Updates kw rank from finished SERP request.
//
// If this was after fetching parallel results,
// it will be also set by `updateRankAfterParallelRequests`.
// It would be pretty complicated to cancel/block this action
// in that case, and double set of the same rank value is not
// a big deal. UI won't rerender anyway.
const updateKeywordRank = handleUncaught(function* updateKeywordRank(action) {
    const { rank, rankUpdatedAt, contentTypes } = action.payload;
    const currentKeywordId = yield select(currentKeywordIdSelector);

    if (!isNil(currentKeywordId)) {
        yield put(receivedKeywordRankAction(currentKeywordId, rank, rankUpdatedAt, contentTypes));
        yield put(receivedKwGapKeywordRankAction(currentKeywordId, rank, rankUpdatedAt, contentTypes));
    }
});

const updateDefaultLocation = handleUncaught(function* updateDefaultLocation(action) {
    const { params } = action.payload;
    yield put(setDefaultGoogleLocation(params.location));
});

const updateContentTypeFromSerps = handleUncaught(function* updateContentTypeFromSerps(action) {
    const { kwId, contentTypes } = action.payload;
    yield put(
        receivedSingleContentTypesAction({
            [kwId]: contentTypes ?? [],
        }),
    );
});

const updateKwOpportunitiesFromSerps = handleUncaught(function* updateKwOpportunitiesFromSerps(action) {
    const { kwId, kwOpps } = action.payload;
    if (kwOpps) {
        yield put(
            receivedSingleKwOpportunitiesAction({
                [kwId]: kwOpps ?? undefined,
            }),
        );
    }
});

const updateContentTypeFromRefresh = handleUncaught(function* updateContentTypeFromRefresh(action) {
    const { id, contentTypes } = action.payload;
    yield put(
        receivedSingleContentTypesAction({
            [id]: contentTypes ?? [],
        }),
    );
});

const updateKwOpportunitiesFromRefresh = handleUncaught(function* updateKwOpportunitiesFromRefresh(action) {
    const { id, kwOpps } = action.payload;
    if (kwOpps) {
        yield put(
            receivedSingleKwOpportunitiesAction({
                [id]: kwOpps ?? undefined,
            }),
        );
    }
});

/**
 * NOTE:
 *
 * Updates rank after parallel related/serp results requests.
 * Waits for two races with success/error fetchig actions.
 * This way it covers both cases. When first finishes SERP request
 * and also when first finishes related request.
 */
const updateRankAfterParallelRequests = handleUncaught(function* updateRankAfterParallelRequests() {
    // Wait for both data results received (in no particular order)
    // while racing for either success or error.
    const [serpAction, resultAction] = yield all([
        race({
            success: take(ActionTypes.DATA_SERP_RESULTS_RECEIVED),
            error: take(ActionTypes.DATA_SERP_RESULTS_ERROR),
        }),
        race({
            success: take(ActionTypes.DATA_RESULTS_RECEIVED),
            error: take(ActionTypes.DATA_RESULTS_ERROR),
        }),
    ]);

    // Continue only if both finished with success
    if (!isNil(serpAction.success) && !isNil(resultAction.success)) {
        // Get rank from serp payload and set it for kwId from related payload
        const { rank, rankUpdatedAt, contentTypes } = serpAction.success.payload;
        const firstKw = resultAction.success.payload.data[0];

        if (!isNil(firstKw)) {
            yield put(receivedKeywordRankAction(firstKw.id, rank, rankUpdatedAt, contentTypes));
            yield put(receivedKwGapKeywordRankAction(firstKw.id, rank, rankUpdatedAt, contentTypes));
        }
    }
});

const checkNewAppVersion = handleUncaught(function* checkNewAppVersion(action, retrying = false) {
    const { result, _timeout } = yield race({
        result: call(VersionSource.get),
        _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
    });

    if (!isNil(result)) {
        const { error, payload } = result;

        if (!error) {
            let newAppVersion;
            const currentAppVersion = APP_CONFIG.APP_VERSION;

            if (APP_CONFIG.production()) {
                newAppVersion = payload.kwfinder;
            } else {
                newAppVersion = payload.kwfinderBeta;
            }

            if (!isNil(newAppVersion) && !isNil(currentAppVersion)) {
                const newAppVersionParts = newAppVersion.split('.');
                const newAppVersionMajor = parseInt(newAppVersionParts[0], 10);
                const newAppVersionMinor = parseInt(newAppVersionParts[1], 10);

                const currentAppVersionParts = currentAppVersion.split('.');
                const currentAppVersionMajor = parseInt(currentAppVersionParts[0], 10);
                const currentAppVersionMinor = parseInt(currentAppVersionParts[1], 10);

                if (newAppVersionMajor > currentAppVersionMajor || newAppVersionMinor > currentAppVersionMinor) {
                    const notificationShown = yield select(newVersionNotificationShownSelector);

                    // Only show if not already shown during this session
                    if (notificationShown === false) {
                        // We have an older version of application.
                        // Show a special notification.
                        yield call(
                            showInfoNotification,
                            <div>
                                <h4 class="font-14">UPDATE AVAILABLE 🤩</h4>

                                <p>
                                    Please, reload the application to get newest features and prevent possible glitches.
                                </p>

                                <br />

                                <button
                                    class="mg-btn is-xsmall is-orange is-gradient mg-margin-t-5"
                                    onclick="location.reload();"
                                    type="button"
                                >
                                    Reload now
                                </button>
                            </div>,
                        );

                        yield put(setNewVersionNotificationShown());
                    }
                }
            }
        } else {
            switch (payload.status) {
                case ErrorCodes.FETCH_ERROR: {
                    if (retrying !== true) {
                        // Wait for CONNECTION_RETRY_DELAY and try again
                        yield delay(Defaults.CONNECTION_RETRY_DELAY);
                        yield call(checkNewAppVersion, action, true);
                    }
                    break;
                }
                case ErrorCodes.SERVICE_UNAVAILABLE: {
                    if (retrying === true) {
                        yield call(logError, 'FetchAppVersionDataSaga', payload);
                    } else {
                        yield call(checkNewAppVersion, action, true);
                    }
                    break;
                }
                case ErrorCodes.INTERNAL_SERVER_ERROR:
                default: {
                    if (retrying === true) {
                        yield call(logError, 'FetchAppVersionDataSaga', payload);
                    } else {
                        yield call(checkNewAppVersion, action, true);
                    }
                    break;
                }
            }
        }
    } else {
        yield call(logError, 'FetchAppVersionDataSaga', INTERNAL_TIMEOUT_ERROR_PAYLOAD);
    }
});

export const fetchCurrencyRates = handleUncaught(
    function* fetchCurrencyRates(action, retrying = false) {
        const accessToken = yield select(accessTokenSelector);

        if (!isNil(accessToken)) {
            yield put(fetchingCurrencyRatesAction());

            const { result, _timeout } = yield race({
                result: call(CurrencyRatesSource.getData, accessToken),
                _timeout: delay(Defaults.MAX_REQUEST_TIMEOUT),
            });

            if (!isNil(result)) {
                const { error, payload } = result;

                if (!error) {
                    const selectedCurrency = yield select(currencySelector);
                    yield put(receivedCurrencyRatesAction(payload));
                    yield put(
                        setCurrency(payload.currencies.find(currency => currency.code === selectedCurrency.code)),
                    );
                } else {
                    switch (payload.status) {
                        case ErrorCodes.FETCH_ERROR: {
                            if (retrying === true) {
                                yield put(errorCurrencyRatesAction(payload));
                                yield put(showNoConnectionMessage());
                            } else {
                                // Wait for CONNECTION_RETRY_DELAY and try again
                                yield delay(Defaults.CONNECTION_RETRY_DELAY);
                                yield call(fetchCurrencyRates, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.TOO_MANY_REQUESTS: {
                            // Background fetch for the currency switcher - never worth a modal
                            yield put(errorCurrencyRatesAction(payload));
                            break;
                        }
                        case ErrorCodes.SERVICE_UNAVAILABLE: {
                            if (retrying === true) {
                                yield put(errorCurrencyRatesAction(payload));
                                yield put(showFailureMessage({ details: Strings.messages.failure.maintenance }));
                                yield call(logError, 'fetchCurrencyRatesSaga', payload);
                            } else {
                                yield call(fetchCurrencyRates, action, true);
                            }
                            break;
                        }
                        case ErrorCodes.INTERNAL_SERVER_ERROR:
                        default: {
                            if (retrying === true) {
                                yield put(errorCurrencyRatesAction(payload));
                                yield put(
                                    showFailureMessage({ details: Strings.messages.failure.fetch_currencies_error }),
                                );
                                yield call(logError, 'fetchCurrencyRatesSaga', payload);
                            } else {
                                yield call(fetchCurrencyRates, action, true);
                            }
                            break;
                        }
                    }
                }
            }
        }
    },
    function* onError() {
        yield put(errorHistoryAction(INTERNAL_UNCAUGHT_ERROR_PAYLOAD));
        yield put(showFailureMessage({ details: Strings.messages.failure.fetch_currencies_error }));
    },
);

function* watchResultsRequests() {
    yield takeLatest(ActionTypes.DATA_RESULTS_REQUESTED, fetchResults);
    yield takeLatest(ActionTypes.DATA_RESULTS_REQUESTED, updateRankAfterParallelRequests);
}

function* watchLoadMoreKeywordsByDomainResultsRequests() {
    yield takeLatest(
        ActionTypes.DATA_LOAD_MORE_KEYWORDS_BY_DOMAIN_RESULTS_REQUESTED,
        fetchLoadMoreKeywordsByDomainResults,
    );
}

function* watchKeywordsByDomainResultsRequests() {
    yield takeLatest(ActionTypes.DATA_KEYWORDS_BY_DOMAIN_RESULTS_REQUESTED, fetchKeywordsByDomainResults);
}

function* watchCompetitorsByDomainResultsRequests() {
    yield takeLatest(ActionTypes.DATA_COMPETITOR_BY_DOMAINS_RESULTS_REQUESTED, fetchCompetitorsByDomainResults);
}

function* watchKeywordGapResultsRequests() {
    yield takeLatest(ActionTypes.DATA_KEYWORD_GAP_RESULTS_REQUESTED, fetchKeywordGapResults);
}

function* watchLoadMoreKeywordGapResultsRequests() {
    yield takeLatest(ActionTypes.DATA_LOAD_MORE_KEYWORD_GAP_RESULTS_REQUESTED, fetchLoadMoreKeywordGapResults);
}

function* watchGoogleAutocompleteResultsRequests() {
    yield takeLatest(ActionTypes.DATA_RESULTS_GOOGLE_AUTOCOMPLETE_REQUESTED, fetchGoogleAutocompleteResults);
}

function* watchGoogleQuestionsResultsRequests() {
    yield takeLatest(ActionTypes.DATA_RESULTS_GOOGLE_QUESTIONS_REQUESTED, fetchGoogleQuestionsResults);
}

function* watchSerpResultsRequests() {
    yield takeLatest(ActionTypes.DATA_SERP_RESULTS_REQUESTED, fetchSerpResults, {
        disableCache: false,
        retrying: false,
    });
}

function* watchUncachedSerpResultsRequests() {
    yield takeLatest(ActionTypes.DATA_SERP_RESULTS_UNCACHED_REQUESTED, fetchSerpResults, {
        disableCache: true,
        retrying: false,
    });
}

function* watchSerpResultsMoreRequests() {
    yield takeLatest(ActionTypes.DATA_SERP_RESULTS_MORE_REQUESTED, fetchSerpResultsMore);
}

function* watchLocationsRequests() {
    yield takeLatest(ActionTypes.DATA_LOCATIONS_REQUESTED, fetchLocations);
}

// NOTE: All these requests are updating the current keyword which might need
// to fetch trends data.
function* watchTrendsRequests() {
    yield takeLatest(ActionTypes.DATA_IMPORT_RECEIVED, fetchTrends);
    yield takeLatest(ActionTypes.DATA_LISTS_KEYWORDS_RECEIVED, fetchTrends);
    yield takeLatest(ActionTypes.DATA_RESULTS_RECEIVED, fetchTrends);
    yield takeLatest(ActionTypes.DATA_KEYWORD_GAP_RESULTS_RECEIVED, fetchTrends);
    yield takeLatest(ActionTypes.UI_DASHBOARD_CURRENT_KEYWORD_ID_SET, fetchTrends);
    yield takeLatest(ActionTypes.UI_DEFAULTS_SHARED_CURRENT_KEYWORD_GRAPH_SET, fetchTrends);
}

function* watchListDataRequests() {
    yield takeLatest(ActionTypes.DATA_LISTS_REQUESTED, fetchListData);
}

function* watchListDeleteRequests() {
    yield takeLatest(ActionTypes.DATA_LISTS_DELETE_REQUESTED, deleteList);
}

function* watchListRenameRequests() {
    yield takeLatest(ActionTypes.DATA_LISTS_RENAME_REQUESTED, renameList);
}

function* watchSelectedKwExportRequests() {
    yield takeLatest(ActionTypes.DATA_SELECTED_KW_EXPORT_REQUESTED, exportSelectedKeywordsData);
}

function* watchSerpResultsReceived() {
    yield takeLatest(ActionTypes.DATA_SERP_RESULTS_RECEIVED, updateKeywordRank);
    yield takeLatest(ActionTypes.DATA_SERP_RESULTS_RECEIVED, updateContentTypeFromSerps);
    yield takeLatest(ActionTypes.DATA_SERP_RESULTS_RECEIVED, updateKwOpportunitiesFromSerps);
}

function* watchRefreshRequestsReceived() {
    yield takeLatest(ActionTypes.DATA_RESULTS_KEYWORD_RANK_RECEIVED, updateContentTypeFromRefresh);
    yield takeLatest(ActionTypes.DATA_RESULTS_KEYWORD_RANK_RECEIVED, updateKwOpportunitiesFromRefresh);
}

function* watchResultsReceived() {
    yield takeLatest(ActionTypes.DATA_RESULTS_RECEIVED, updateDefaultLocation);
}

function* watchAddToListRequests() {
    yield takeLatest(ActionTypes.DATA_SELECTED_KW_ADD_TO_LIST_REQUESTED, addOrCreateList);
}

function* watchShowListRequests() {
    yield takeLatest(ActionTypes.DATA_LISTS_KEYWORDS_REQUESTED, loadDashboardWithListData);
}

function* watchHistoryRequests() {
    yield takeLatest(ActionTypes.DATA_HISTORY_REQUESTED, fetchHistoryData);
}

function* watchKdHistoryRequests() {
    yield takeLatest(ActionTypes.DATA_KD_HISTORY_REQUESTED, fetchKdHistoryData);
}

function* watchImportRequests() {
    yield takeLatest(ActionTypes.DATA_IMPORT_REQUESTED, loadDashboardWithImportedData);
}

function* watchImportCurrentKeywordsRequests() {
    yield takeLatest(ActionTypes.DATA_IMPORT_CURRENT_KEYWORDS_REQUESTED, importCurrentKeywords);
}

function* watchListDeleteKeywordsFromCurrentListRequests() {
    yield takeLatest(ActionTypes.DATA_LISTS_CURRENT_DELETE_KEYWORDS_REQUESTED, deleteKeywordsFromCurrentList);
}

function* watchListDeleteKeywordFromListRequests() {
    yield takeLatest(ActionTypes.DATA_LISTS_DELETE_KEYWORD_REQUESTED, deleteKeywordFromList);
}

function* watchHistoryDeleteRequests() {
    yield takeLatest(ActionTypes.DATA_HISTORY_DELETE_REQUESTED, deleteHistoryData);
}

function* watchKdHistoryDeleteRequests() {
    yield takeLatest(ActionTypes.DATA_KD_HISTORY_DELETE_REQUESTED, deleteKdHistoryData);
}

function* watchUrlDataRequests() {
    yield takeLatest(ActionTypes.DATA_URL_DATA_REQUESTED, fetchUrlData);
}

function* watchSearchIntentRequests() {
    yield takeLatest(ActionTypes.DATA_SEARCH_INTENT_REQUESTED, fetchSearchIntent);
}

function* watchKwOpportunitiesRequests() {
    yield takeLatest(ActionTypes.DATA_KW_OPPORTUNITIES_REQUESTED, fetchKwOpportunities);
}

function* watchContentTypesRequests() {
    yield takeLatest(ActionTypes.DATA_CONTENT_TYPES_REQUESTED, fetchContentType);
}

function* watchSerpFeaturesRequests() {
    yield takeLatest(ActionTypes.DATA_SERP_FEATURES_REQUESTED, fetchSerpFeatures);
}

function* watchBulkRefreshRequests() {
    yield takeLatest(ActionTypes.UI_MESSAGES_REFRESH_CONFIRM_PROCEED, bulkFetchSerpResults, {
        disableCache: false,
        retrying: false,
    });
}

function newAppVersionCheckIntervalChannel() {
    return eventChannel(emitter => {
        const intervalId = setInterval(() => {
            emitter({
                intervalId,
            });
        }, Defaults.APP_VERSION_CHECK_INTERVAL);

        return () => {
            clearInterval(intervalId);
        };
    });
}

function* watchNewAppVersionByInterval() {
    const channel = yield call(newAppVersionCheckIntervalChannel);
    yield takeEvery(channel, checkNewAppVersion);
}

export function* fetchAfterLoginData() {
    yield spawn(fetchListData);
    yield spawn(fetchAnnouncements);
    yield spawn(checkNewAppVersion);
    yield spawn(fetchCurrencyRates);
    yield spawn(checkUnleashSession);
}

export function* watchDataRequests() {
    yield spawn(watchAddToListRequests);
    yield spawn(watchKeywordsByDomainResultsRequests);
    yield spawn(watchLoadMoreKeywordsByDomainResultsRequests);
    yield spawn(watchCompetitorsByDomainResultsRequests);
    yield spawn(watchKeywordGapResultsRequests);
    yield spawn(watchLoadMoreKeywordGapResultsRequests);
    yield spawn(watchGoogleAutocompleteResultsRequests);
    yield spawn(watchGoogleQuestionsResultsRequests);
    yield spawn(watchHistoryRequests);
    yield spawn(watchKdHistoryRequests);
    yield spawn(watchImportCurrentKeywordsRequests);
    yield spawn(watchImportRequests);
    yield spawn(watchListDataRequests);
    yield spawn(watchListDeleteKeywordsFromCurrentListRequests);
    yield spawn(watchListDeleteRequests);
    yield spawn(watchListRenameRequests);
    yield spawn(watchLocationsRequests);
    yield spawn(watchResultsReceived);
    yield spawn(watchResultsRequests);
    yield spawn(watchSerpResultsMoreRequests);
    yield spawn(watchSerpResultsReceived);
    yield spawn(watchSerpResultsRequests);
    yield spawn(watchShowListRequests);
    yield spawn(watchTrendsRequests);
    yield spawn(watchUncachedSerpResultsRequests);
    yield spawn(watchSelectedKwExportRequests);
    yield spawn(watchHistoryDeleteRequests);
    yield spawn(watchKdHistoryDeleteRequests);
    yield spawn(watchBulkRefreshRequests);
    yield spawn(watchListDeleteKeywordFromListRequests);
    yield spawn(watchNewAppVersionByInterval);
    yield spawn(watchUrlDataRequests);
    yield spawn(watchRefreshRequestsReceived);
    yield spawn(watchSearchIntentRequests);
    yield spawn(watchKwOpportunitiesRequests);
    yield spawn(watchContentTypesRequests);
    yield spawn(watchSerpFeaturesRequests);
}
